import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, GitBranch, Calendar, Users, CheckSquare, DollarSign, Clock, Sparkles, Plus, Search, ChevronRight,
  CheckCircle2, AlertTriangle, Check, Activity, FileText, X, Trash2, Settings, Edit3, AlertCircle,
  MoreHorizontal, PlayCircle, Layers, Bot, Send, TrendingUp, BarChart3, PieChart, Info, FolderOpen, Paperclip,
  CheckCircle, RefreshCw, User, HelpCircle, Shield, Briefcase, PlusCircle, ArrowUpRight,
  MessageSquare, Heart, Share2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { DndContext, useDroppable, useDraggable, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import ScrollableTabsContainer from './common/ScrollableTabsContainer';
import { useProjectDashboard, useUpdateProject, useCreateProjectMember, useDeleteProjectMember, useProjectMembers, useProjectComments, useCreateProjectComment, useDeleteProjectComment, useProjectSprints, useProjectMilestones, useProjectAuditLogs, useCreateReaction, useDeleteReaction, useProjectDocuments, useProjectFinance } from '../hooks/projects/useProjects';
import { useCreateDocument, useUpdateDocument, useDeleteDocument } from '@/api/queries';
import { mapProjectDashboard } from '../utils/dashboardMappers';
import { showError, showSuccess } from '../lib/alerts';
import Swal from 'sweetalert2';

interface Comment {
  id: number;
  backendId?: string;
  author: string;
  role: string;
  photo: string | null;
  text: string;
  time: string;
  timelineGroup: 'Hoje' | 'Ontem' | 'Esta Semana' | 'Este Mês' | 'Mais Antigos';
  type: 'comment' | 'idea' | 'decision' | 'document' | 'image' | 'indicator' | 'deploy' | 'event' | 'warning' | 'approval' | 'rejection' | 'pin' | 'ai' | 'achievement' | 'meeting' | 'system';
  votes: number;
  likedByCurrentUser?: boolean;
  replies?: Array<{
    id: number;
    author: string;
    role: string;
    photo: string | null;
    text: string;
    time: string;
    likedByCurrentUser?: boolean;
    votes?: number;
    replies?: Array<{
      id: number;
      author: string;
      role: string;
      photo: string | null;
      text: string;
      time: string;
    }>;
  }>;
  tags?: string[];
  decidedBy?: string;
  approvedBy?: string;
  impact?: 'Alto' | 'Médio' | 'Baixo';
  relationships?: string[];
  isPinned?: boolean;
  voting?: {
    agree: number;
    disagree: number;
    greatIdea: number;
    userVote?: 'agree' | 'disagree' | 'greatIdea';
  };
  attachments?: Array<{
    name: string;
    size: string;
    type: 'pdf' | 'image' | 'xlsx' | 'docx' | 'link';
    url?: string;
  }>;
  indicator?: {
    metric: string;
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  corporateReactions?: {
    agree: number;
    excellent: number;
    goodIdea: number;
    attention: number;
    approved: number;
    review: number;
    priority: number;
    userReactions?: string[];
  };
}

const getMemberPhoto = (name?: any): string | null => {
  if (!name || typeof name !== 'string') return null;
  const lower = name.toLowerCase();
  if (lower.includes('joão') || lower.includes('joao')) {
    return 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('maria')) {
    return 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('pedro')) {
    return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('ana')) {
    return 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('lucas')) {
    return 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('clara')) {
    return 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('carlos')) {
    return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('marcos')) {
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80';
  }
  const fallbacks = [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80'
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return fallbacks[sum % fallbacks.length];
};

const parseDueDate = (dueDate?: string): Date | null => {
  if (!dueDate) return null;
  const isoMatch = /^\d{4}-\d{2}-\d{2}/;
  if (isoMatch.test(dueDate)) return new Date(dueDate);

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const brResult = dueDate.match(brMatch);
  if (brResult) {
    const [, day, month, year] = brResult;
    return new Date(`${year}-${month}-${day}`);
  }

  const cleaned = dueDate.replace(/(de|\.)/g, '').replace(/\s+/g, ' ').trim();
  return new Date(cleaned);
};

const formatDueStatus = (dueDate?: string): string => {
  const due = parseDueDate(dueDate);
  if (!due || Number.isNaN(due.getTime())) return 'Sem prazo definido';
  const today = new Date();
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `Atrasado ${Math.abs(diffDays)}d`;
  if (diffDays === 0) return 'Entrega hoje';
  if (diffDays === 1) return 'Entrega amanhã';
  return `Entrega em ${diffDays}d`;
};

const getPriorityBadgeClasses = (priority?: string): string => {
  if (!priority) return 'bg-slate-100 text-slate-700 border border-slate-200';
  const norm = priority.toLowerCase();
  if (norm.includes('crítica') || norm.includes('critical')) return 'bg-rose-50 text-rose-700 border border-rose-100';
  if (norm.includes('alta') || norm.includes('high')) return 'bg-amber-50 text-amber-700 border border-amber-100';
  if (norm.includes('média') || norm.includes('medium')) return 'bg-sky-50 text-sky-700 border border-sky-100';
  return 'bg-slate-100 text-slate-700 border border-slate-200';
};

const getDueBadgeClass = (dueDate?: string): string => {
  if (!dueDate) return 'bg-slate-100 text-slate-600 border border-slate-200';
  const due = parseDueDate(dueDate);
  if (!due || Number.isNaN(due.getTime())) return 'bg-slate-100 text-slate-600 border border-slate-200';
  const today = new Date();
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'bg-rose-50 text-rose-700 border border-rose-100';
  if (diffDays <= 2) return 'bg-amber-50 text-amber-700 border border-amber-100';
  return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
};

type ProjectWorkspaceTabId =
  | 'resumo'
  | 'kanban'
  | 'roadmap'
  | 'sprints'
  | 'equipe'
  | 'discussions'
  | 'tarefas'
  | 'documentacao'
  | 'arquivos'
  | 'financeiro'
  | 'ia'
  | 'configuracoes';

const PROJECT_WORKSPACE_GROUPS = [
  {
    label: 'Visão geral',
    tabs: [{ id: 'resumo', label: 'Resumo', icon: BarChart3 }],
  },
  {
    label: 'Planejamento',
    tabs: [
      { id: 'roadmap', label: 'Roadmap', icon: Calendar },
      { id: 'sprints', label: 'Sprints', icon: RefreshCw },
    ],
  },
  {
    label: 'Execução',
    tabs: [
      { id: 'kanban', label: 'Board', icon: Layers },
      { id: 'tarefas', label: 'Lista', icon: CheckSquare },
    ],
  },
  {
    label: 'Colaboração',
    tabs: [
      { id: 'equipe', label: 'Equipe', icon: Users },
      { id: 'discussions', label: 'Atividades', icon: MessageSquare },
    ],
  },
  {
    label: 'Conhecimento',
    tabs: [
      { id: 'documentacao', label: 'Documentação', icon: FileText },
      { id: 'arquivos', label: 'Arquivos', icon: Paperclip },
    ],
  },
  {
    label: 'Controle',
    tabs: [
      { id: 'financeiro', label: 'Financeiro', icon: DollarSign },
      { id: 'ia', label: 'IA Cyzor', icon: Sparkles },
      { id: 'configuracoes', label: 'Configurações', icon: Settings },
    ],
  },
] as const;

interface DroppableColumnProps {
  column: { id: string; label: string; color: string };
  tasks: any[];
  onUpdateStatus: (taskId: string | number, status: string) => Promise<void>;
  onDeleteTask: (taskId: number) => Promise<void>;
  activeDragTaskId: string | number | null;
}

function DroppableKanbanColumn({ column, tasks, onUpdateStatus, onDeleteTask, activeDragTaskId }: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      ref={setNodeRef}
      className={`border rounded-2xl p-4 flex flex-col gap-3 min-h-[350px] transition-colors ${column.color} ${isOver ? 'ring-2 ring-indigo-400/40' : ''}`}
    >
      <div className="flex justify-between items-center">
        <span className="text-xs font-black uppercase text-neutral-700">{column.label}</span>
        <span className="text-[10px] font-black px-2 py-0.5 bg-white border border-neutral-200 text-neutral-600 rounded-md">
          {tasks.length}
        </span>
      </div>

      <div className="flex flex-col gap-2 overflow-y-auto max-h-[400px]">
        {tasks.map((t) => (
          <DraggableTaskCard
            key={t.id}
            task={t}
            columnLabel={column.label}
            onUpdateStatus={onUpdateStatus}
            onDeleteTask={onDeleteTask}
            isDragging={activeDragTaskId === t.id}
          />
        ))}

        {tasks.length === 0 && (
          <p className="text-[10px] text-neutral-400 font-bold text-center py-6">Sem itens</p>
        )}
      </div>
    </div>
  );
}

interface DraggableTaskCardProps {
  task: any;
  columnLabel: string;
  onUpdateStatus: (taskId: string | number, status: string) => Promise<void>;
  onDeleteTask: (taskId: number) => Promise<void>;
  isDragging: boolean;
}

function DraggableTaskCard({ task, columnLabel, onUpdateStatus, onDeleteTask, isDragging }: DraggableTaskCardProps) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: String(task.id) });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="bg-white border border-neutral-200 p-3.5 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-neutral-300 hover:shadow-[0_5px_16px_rgba(15,23,42,0.08)] transition-all group cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="block text-[9px] font-bold uppercase tracking-[0.22em] text-neutral-400 mb-1.5">{columnLabel}</span>
          <h4 className="text-[13px] font-extrabold text-neutral-900 leading-snug line-clamp-2">{task.title}</h4>
        </div>
        <span className={`shrink-0 text-[9px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${getPriorityBadgeClasses(task.priority)}`}>
          {task.priority || 'Média'}
        </span>
      </div>

      <p className="mt-2.5 text-[11px] text-neutral-500 leading-4 line-clamp-2 min-h-8">
        {task.description || 'Tarefa operacional do projeto.'}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[9px]">
        <span className={`px-2 py-1 rounded-lg font-bold ${getDueBadgeClass(task.dueDate)}`}>
          {formatDueStatus(task.dueDate)}
        </span>
        {task.dueDate && (
          <span className="px-2 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200">
            {new Date(task.dueDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
          </span>
        )}
        {task.estimateHours && (
          <span className="px-2 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200">
            {task.estimateHours}h estimadas
          </span>
        )}
        {task.category && (
          <span className="px-2 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200">
            {task.category}
          </span>
        )}
        {task.assigneeName && (
          <span className="max-w-full truncate px-2 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200">
            Responsável: {task.assigneeName}
          </span>
        )}
      </div>

      <div className="mt-3.5 pt-2.5 border-t border-neutral-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[9px] text-neutral-400">
          <span className="uppercase tracking-[0.16em] font-bold">#{String(task.id).slice(-8)}</span>
          <span className="h-3 w-px bg-neutral-200" />
          <span className="font-semibold">{columnLabel}</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {columnLabel !== 'Concluído' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdateStatus(task.id, columnLabel === 'Backlog' ? 'IN_PROGRESS' : columnLabel === 'Em Andamento' ? 'REVIEW' : 'DONE');
              }}
              className="px-2 py-1 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all text-[9px] font-bold"
              title="Avançar status"
            >
              Avançar
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteTask(task.id);
            }}
              className="px-2 py-1 bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100 transition-all text-[9px] font-bold"
            title="Deletar"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProjetoWorkspaceViewProps {
  setCurrentView: (view: any) => void;
}

export default function ProjetoWorkspaceView({ setCurrentView }: ProjetoWorkspaceViewProps) {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const { activeWorkspace, syncSaaSState, user } = useAuth();

const projectId = String(globalFilters.projectId || '');

  // React Query - Projeto Dashboard (V2) unificado
  const {
    data: dashboard,
    isLoading,
    isError,
    refetch: refetchDashboard,
  } = useProjectDashboard(projectId);

  // React Query - Mutation de atualização do projeto
  const updateProjectMutation = useUpdateProject();

  // React Query - Dados específicos do projeto
  const { data: apiSprints, refetch: refetchSprints } = useProjectSprints(projectId);
  const { data: apiMilestones, refetch: refetchMilestones } = useProjectMilestones(projectId);
  const { data: apiAuditLogs, refetch: refetchAuditLogs } = useProjectAuditLogs(projectId);
  const { data: apiFinance } = useProjectFinance(projectId);

  // Apply mapper to normalize dashboard DTO → UI shape
  const mappedDashboard = useMemo(() => mapProjectDashboard(dashboard), [dashboard]);

  // Derive project object from mapped dashboard
  const project = useMemo(() => {
    if (!mappedDashboard) return null;
    return {
      id: mappedDashboard.id,
      name: mappedDashboard.name,
      description: mappedDashboard.description,
      status: mappedDashboard.statusLabel,
      progress: mappedDashboard.progress ?? 0,
      budget: mappedDashboard.budget ?? 0,
      dueDate: mappedDashboard.dueDate,
      priority: mappedDashboard.priority || 'Média',
      owner: mappedDashboard.owner || '—',
      company: mappedDashboard.company || '',
    };
  }, [mappedDashboard]);

  // Derived collections from mapped dashboard
  const projectTasks = useMemo(() => mappedDashboard?.tasks || [], [mappedDashboard]);
  const projectDocs = useMemo(() => mappedDashboard?.documents || [], [mappedDashboard]);
  const projectMembers = useMemo(() => mappedDashboard?.members || [], [mappedDashboard]);
  const projectMilestones = useMemo(() => mappedDashboard?.milestones || [], [mappedDashboard]);
  const projectSprints = useMemo(() => mappedDashboard?.sprints || [], [mappedDashboard]);
  const projectFinance = useMemo(() => (apiFinance as any) || [], [apiFinance]);
  const projectCostTotal = useMemo(() => {
    return projectFinance
      .filter((entry: any) => ['DEBIT', 'DEBITO', 'EXPENSE', 'DESPESA'].includes(String(entry.type || '').toUpperCase()))
      .reduce((total: number, entry: any) => total + Number(entry.amount || 0), 0);
  }, [projectFinance]);

  const projectCompany = useMemo(() => {
    return mappedDashboard?.customer ? { id: mappedDashboard.customer.id, name: mappedDashboard.customer.name } : null;
  }, [mappedDashboard]);

  // Synchronize dynamic updates
  const syncWorkspaceData = async () => {
    await Promise.all([refetchDashboard(), syncSaaSState()]);
    showSuccess('Workspace do projeto sincronizado!');
  };

  // Local state for tabs
  const [activeTab, setActiveTab] = useState<ProjectWorkspaceTabId>('resumo');

  // Kanban drag-and-drop state
  const [activeDragTaskId, setActiveDragTaskId] = useState<string | number | null>(null);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('');
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState('');
  const [taskSort, setTaskSort] = useState<'createdAt' | 'dueDate' | 'priority'>('createdAt');

  const executionTasks = useMemo(() => {
    const priorityOrder: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
    const normalizedSearch = taskSearch.trim().toLowerCase();
    const filtered = projectTasks.filter((task: any) => {
      const searchable = `${task.title || ''} ${task.description || ''}`.toLowerCase();
      const matchesSearch = !normalizedSearch || searchable.includes(normalizedSearch);
      const matchesStatus = !taskStatusFilter || task.status === taskStatusFilter;
      const matchesPriority = !taskPriorityFilter || task.priority === taskPriorityFilter;
      const matchesAssignee = !taskAssigneeFilter || (task.assigneeName || '') === taskAssigneeFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
    });

    return [...filtered].sort((left: any, right: any) => {
      if (taskSort === 'dueDate') {
        return (left.dueDate || '9999-12-31').localeCompare(right.dueDate || '9999-12-31');
      }
      if (taskSort === 'priority') {
        return (priorityOrder[left.priority] ?? 99) - (priorityOrder[right.priority] ?? 99);
      }
      return String(right.createdAt || '').localeCompare(String(left.createdAt || ''));
    });
  }, [projectTasks, taskSearch, taskStatusFilter, taskPriorityFilter, taskAssigneeFilter, taskSort]);

  const commandCenter = useMemo(() => {
    const today = new Date();
    const todayKey = today.toISOString().split('T')[0];
    const isDone = (task: any) => task.status === 'DONE' || task.status === 'CONCLUIDO';
    const datedTasks = projectTasks.filter((task: any) => task.dueDate);
    const overdueTasks = datedTasks.filter((task: any) => !isDone(task) && parseDueDate(task.dueDate) && parseDueDate(task.dueDate)! < today);
    const dueTodayTasks = datedTasks.filter((task: any) => !isDone(task) && task.dueDate === todayKey);
    const unassignedTasks = projectTasks.filter((task: any) => !task.assigneeName);
    const blockedTasks = projectTasks.filter((task: any) => task.status === 'BLOCKED');
    const currentUserKeys = [user?.id, user?.email, user?.fullName, (user as any)?.displayName].filter(Boolean).map(String);
    const myTasks = projectTasks.filter((task: any) => currentUserKeys.includes(String(task.assigneeId || task.assigneeName || '')));
    const revenueTotal = projectFinance
      .filter((entry: any) => ['CREDIT', 'CREDITO', 'REVENUE', 'RECEITA'].includes(String(entry.type || '').toUpperCase()))
      .reduce((total: number, entry: any) => total + Number(entry.amount || 0), 0);
    const budget = Number(project?.budget || 0);
    const upcoming = [
      ...datedTasks.filter((task: any) => !isDone(task)).map((task: any) => ({
        id: `task-${task.id}`,
        label: task.title,
        date: task.dueDate,
        kind: 'Tarefa',
        action: () => {
          setTaskSearch(task.title || '');
          setActiveTab('tarefas');
        },
      })),
      ...projectMilestones.filter((milestone: any) => milestone.dueDate && !milestone.completedAt).map((milestone: any) => ({
        id: `milestone-${milestone.id}`,
        label: `Marco: ${milestone.name}`,
        date: milestone.dueDate,
        kind: 'Marco',
        action: () => setActiveTab('roadmap'),
      })),
      ...projectSprints.filter((sprint: any) => sprint.endDate && sprint.status === 'ACTIVE').map((sprint: any) => ({
        id: `sprint-${sprint.id}`,
        label: `Fim da sprint: ${sprint.name}`,
        date: sprint.endDate,
        kind: 'Sprint',
        action: () => setActiveTab('sprints'),
      })),
    ].sort((left, right) => String(left.date).localeCompare(String(right.date))).slice(0, 6);

    return {
      completed: projectTasks.filter(isDone).length,
      pending: projectTasks.filter((task: any) => !isDone(task)).length,
      overdueTasks,
      dueTodayTasks,
      unassignedTasks,
      blockedTasks,
      myTasks,
      upcoming,
      execution: {
        todo: projectTasks.filter((task: any) => task.status === 'TODO' || task.status === 'backlog').length,
        inProgress: projectTasks.filter((task: any) => task.status === 'IN_PROGRESS' || task.status === 'desenvolvimento').length,
        review: projectTasks.filter((task: any) => task.status === 'REVIEW' || task.status === 'review').length,
        done: projectTasks.filter(isDone).length,
      },
      activeSprint: projectSprints.find((sprint: any) => sprint.status === 'ACTIVE'),
      revenueTotal,
      budget,
      financialAlert: budget > 0 && projectCostTotal > budget,
    };
  }, [projectTasks, projectMilestones, projectSprints, projectFinance, projectCostTotal, project?.budget, user]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragTaskId(event.active.id);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragTaskId(null);

    if (!over || active.id === over.id) return;

    const taskId = active.id;
    const newStatus = String(over.id);

    const validStatuses = ['TODO', 'IN_PROGRESS', 'REVIEW', 'DONE'];
    if (!validStatuses.includes(newStatus)) return;

    try {
      await handleUpdateTaskStatus(taskId, newStatus);
      showSuccess(`Tarefa movida para ${newStatus === 'TODO' ? 'Backlog' : newStatus === 'IN_PROGRESS' ? 'Em Andamento' : newStatus === 'REVIEW' ? 'Review' : 'Concluído'}`);
    } catch {
      showError('Erro ao atualizar status da tarefa.');
    }
  };

  // Discussions & Social Feed States
  const [newCommentText, setNewCommentText] = useState('');
  const [commentType, setCommentType] = useState<Comment['type']>('comment');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeReactionPickerId, setActiveReactionPickerId] = useState<number | null>(null);

  const { data: apiComments, refetch: refetchComments } = useProjectComments(projectId);
  const createCommentMutation = useCreateProjectComment(projectId);
  const deleteCommentMutation = useDeleteProjectComment(projectId);
  const createReactionMutation = useCreateReaction();
  const deleteReactionMutation = useDeleteReaction();

  const [comments, setComments] = useState<Comment[]>([]);

  useEffect(() => {
    const commentsData = (apiComments as any)?.data || apiComments || [];
    if (commentsData.length > 0) {
      const mapped = commentsData.map((c: any) => ({
        id: Number(c.id) || Date.now() + Math.random(),
        backendId: c.id,
        author: user?.fullName || 'Usuário',
        role: 'Membro da Equipe',
        photo: user?.avatarUrl || getMemberPhoto(user?.fullName || ''),
        text: c.content,
        time: c.createdAt ? new Date(c.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—',
        timelineGroup: 'Hoje' as const,
        type: 'comment' as const,
        votes: 0,
        likedByCurrentUser: false,
        replies: [],
        tags: c.mentions || [],
        corporateReactions: {
          agree: 0, excellent: 0, goodIdea: 0, attention: 0, approved: 0, review: 0, priority: 0, userReactions: []
        },
      }));
      setComments(mapped);
    }
  }, [apiComments, user]);

  const [selectedNewCommentTags, setSelectedNewCommentTags] = useState<string[]>([]);
  const [discussionFilterTag, setDiscussionFilterTag] = useState<string | null>(null);
  const [discussionFilterType, setDiscussionFilterType] = useState<string>('all');
  const [activeReplyBoxCommentId, setActiveReplyBoxCommentId] = useState<number | null>(null);
  const [newReplyTexts, setNewReplyTexts] = useState<{ [commentId: number]: string }>({});

  // Dynamic Social Feed Filtering and Search logic
  const filteredComments = useMemo(() => {
    return comments.filter(c => {
      // Type filter
      if (discussionFilterType !== 'all') {
        if (discussionFilterType === 'system' && c.type !== 'system') return false;
        if (discussionFilterType === 'decision' && c.type !== 'decision') return false;
        if (discussionFilterType === 'suggestion' && c.type !== 'idea') return false;
        if (discussionFilterType === 'document' && c.type !== 'document' && c.type !== 'image') return false;
        if (discussionFilterType === 'comment' && c.type !== 'comment') return false;
        if (discussionFilterType === 'ia' && c.type !== 'ai') return false;
        if (discussionFilterType === 'approval' && c.type !== 'approval' && c.type !== 'rejection') return false;
        if (discussionFilterType === 'event' && c.type !== 'event' && c.type !== 'meeting' && c.type !== 'deploy') return false;
      }
      // Tag filter
      if (discussionFilterTag) {
        if (!c.tags || !c.tags.includes(discussionFilterTag)) {
          return false;
        }
      }
      // Search Query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const textMatch = c.text.toLowerCase().includes(query);
        const authorMatch = c.author.toLowerCase().includes(query);
        const roleMatch = c.role.toLowerCase().includes(query);
        const tagMatch = c.tags?.some(t => t.toLowerCase().includes(query)) || false;
        const fileMatch = c.attachments?.some(f => f.name.toLowerCase().includes(query)) || false;
        const commentReplyMatch = c.replies?.some(r => r.text.toLowerCase().includes(query) || r.author.toLowerCase().includes(query)) || false;
        if (!textMatch && !authorMatch && !roleMatch && !tagMatch && !fileMatch && !commentReplyMatch) {
          return false;
        }
      }
      return true;
    });
  }, [comments, discussionFilterType, discussionFilterTag, searchQuery]);

  // Dynamic Popular/Trending Hashtags computation
  const popularTags = useMemo(() => {
    const counts: { [tag: string]: number } = {};
    comments.forEach(c => {
      if (c.tags) {
        c.tags.forEach(t => {
          counts[t] = (counts[t] || 0) + 1;
        });
      }
    });
    return Object.entries(counts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [comments]);

  // Structured Voting Toggle
  const handleToggleStructuredVote = (commentId: number, option: 'agree' | 'disagree' | 'greatIdea') => {
    setComments(prev => prev.map(c => {
      if (c.id === commentId && c.voting) {
        const currentVote = c.voting.userVote;
        const updatedVoting = { ...c.voting };
        
        // Remove previous vote if any
        if (currentVote) {
          updatedVoting[currentVote] = Math.max(0, updatedVoting[currentVote] - 1);
        }
        
        // If clicking the same option, it toggles off
        if (currentVote === option) {
          updatedVoting.userVote = undefined;
        } else {
          updatedVoting.userVote = option;
          updatedVoting[option] = (updatedVoting[option] || 0) + 1;
        }
        
        return { ...c, voting: updatedVoting };
      }
      return c;
    }));
    showSuccess('Voto computado com sucesso!');
  };

  // Corporate Reactions Toggle
  const handleToggleReaction = async (commentId: number, reaction: string) => {
    const comment = comments.find(c => c.id === commentId);
    if (!comment?.backendId) return;

    const hasReacted = (comment.corporateReactions?.userReactions || []).includes(reaction);

    try {
      if (hasReacted) {
        await deleteReactionMutation.mutateAsync({
          entityType: 'project',
          entityId: comment.backendId,
          type: reaction,
        });
      } else {
        await createReactionMutation.mutateAsync({
          entityType: 'project',
          entityId: comment.backendId,
          type: reaction,
        });
      }

      setComments(prev => prev.map(c => {
        if (c.id === commentId && c.corporateReactions) {
          const reactions = { ...c.corporateReactions } as any;
          const userReactions = [...(reactions.userReactions || [])];
          const index = userReactions.indexOf(reaction);

          if (index > -1) {
            userReactions.splice(index, 1);
            reactions[reaction] = Math.max(0, reactions[reaction] - 1);
          } else {
            userReactions.push(reaction);
            reactions[reaction] = (reactions[reaction] || 0) + 1;
          }

          return { ...c, corporateReactions: { ...reactions, userReactions } };
        }
        return c;
      }));
      setActiveReactionPickerId(null);
    } catch {
      showError('Erro ao atualizar reação.');
    }
  };

  // Toggle Pinned/Fixed post
  const handleTogglePinComment = (commentId: number) => {
    setComments(prev => prev.map(c => {
      if (c.id === commentId) {
        const pinState = !c.isPinned;
        showSuccess(pinState ? '📌 Publicação fixada no topo do feed!' : '📌 Publicação desfixada.');
        return { ...c, isPinned: pinState };
      }
      return c;
    }));
  };

  // Premium Quick Actions stubs
  const handleQuickAction = (commentId: number, actionType: string) => {
    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;

    if (actionType === 'task') {
      Swal.fire({
        title: 'Criar Tarefa do Cyzor',
        html: `
          <div class="text-left font-sans text-xs">
            <p class="mb-2 font-bold text-neutral-600">Descrição da Tarefa baseada no Feed:</p>
            <input id="swal-task-title" class="w-full p-2.5 bg-neutral-50 border rounded-xl mb-3 font-semibold focus:outline-none" value="Análise/Ação: ${comment.text.slice(0, 50)}...">
            <label class="font-bold text-neutral-600">Membro Responsável:</label>
            <select id="swal-task-user" class="w-full p-2.5 bg-neutral-50 border rounded-xl mb-3 font-semibold focus:outline-none">
              <option value="João Silva">João Silva (Ciência de Dados)</option>
              <option value="Maria Santos">Maria Santos (Gestão de Produto)</option>
              <option value="Carlos Executive">Carlos Executive (Sponsorship Executivo)</option>
            </select>
            <label class="font-bold text-neutral-600">Data de Entrega:</label>
            <input type="date" id="swal-task-date" class="w-full p-2.5 bg-neutral-50 border rounded-xl font-semibold focus:outline-none" value="2026-08-05">
          </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Criar e Sincronizar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'bg-indigo-600 text-white rounded-xl px-4 py-2 text-xs font-bold',
          cancelButton: 'bg-neutral-100 text-neutral-600 rounded-xl px-4 py-2 text-xs font-bold'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          showSuccess('🚀 Tarefa gerada automaticamente a partir da Central de Colaboração!');
          // Add system event
          const systemEvent: Comment = {
            id: Date.now(),
            author: 'João Silva',
            role: 'Ciência de Dados',
            photo: getMemberPhoto('João Silva'),
            text: `criou uma nova tarefa baseada nesta atividade: "Análise/Ação: ${comment.text.slice(0, 40)}..." para ser entregue em 05/08/2026.`,
            time: 'Agora mesmo',
            timelineGroup: 'Hoje',
            type: 'system',
            votes: 0,
            replies: []
          };
          setComments(prev => [systemEvent, ...prev]);
        }
      });
    } else if (actionType === 'doc') {
      Swal.fire({
        title: 'Criar Documento no Workspace',
        text: 'Deseja criar um documento na central de documentação oficial associado a esta discussão?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, criar documento',
        cancelButtonText: 'Cancelar'
      }).then(res => {
        if (res.isConfirmed) {
          showSuccess('📄 Documento associado criado com sucesso na aba Documentação!');
          const systemEvent: Comment = {
            id: Date.now(),
            author: 'Maria Santos',
            role: 'Gestão de Produto',
            photo: getMemberPhoto('Maria Santos'),
            text: `gerou o rascunho de documento técnico: "Refinamento - ${comment.author.split(' ')[0]}" na central do projeto.`,
            time: 'Agora mesmo',
            timelineGroup: 'Hoje',
            type: 'system',
            votes: 0,
            replies: []
          };
          setComments(prev => [systemEvent, ...prev]);
        }
      });
    } else if (actionType === 'idea') {
      Swal.fire({
        title: 'Converter em Ideia',
        text: 'Deseja enviar este insight diretamente para o Banco de Inovações do Cyzor?',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Sim, enviar',
        cancelButtonText: 'Cancelar'
      }).then(res => {
        if (res.isConfirmed) {
          showSuccess('💡 Insight cadastrado no Banco de Inovações global!');
        }
      });
    } else if (actionType === 'decision') {
      // Convert to decision
      setComments(prev => prev.map(c => {
        if (c.id === commentId) {
          showSuccess('⚡ Esta publicação foi convertida e homologada como Decisão Executiva do Projeto!');
          return {
            ...c,
            type: 'decision',
            decidedBy: comment.author,
            approvedBy: 'Carlos Executive',
            impact: 'Médio',
            isPinned: true,
            corporateReactions: c.corporateReactions || {
              agree: 1,
              excellent: 1,
              goodIdea: 1,
              attention: 0,
              approved: 1,
              review: 0,
              priority: 1,
              userReactions: []
            }
          };
        }
        return c;
      }));
    }
  };

  // Share handler
  const handleShareComment = (comment: Comment) => {
    navigator.clipboard.writeText(`${window.location.origin}/projects/${projectId}/discussion/${comment.id}`);
    showSuccess('🔗 Link de rastreamento copiado para a área de transferência!');
  };

  // Handle Comment Submission
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;

    try {
      const textHashtags = newCommentText.match(/#\w+/g) || [];
      const combinedTags = Array.from(new Set([
        ...selectedNewCommentTags,
        ...textHashtags
      ]));

      await createCommentMutation.mutateAsync({
        content: newCommentText,
        mentions: combinedTags,
      });

      showSuccess('Publicação criada com sucesso!');
      setNewCommentText('');
      setCommentType('comment');
      setSelectedNewCommentTags([]);
      refetchComments();
      refetchDashboard();
    } catch {
      showError('Erro ao publicar comentário.');
    }
  };

  // Toggle Like state
  const handleToggleLikeComment = (id: number) => {
    setComments(prev => prev.map(c => {
      if (c.id === id) {
        const liked = !c.likedByCurrentUser;
        return {
          ...c,
          likedByCurrentUser: liked,
          votes: liked ? c.votes + 1 : Math.max(0, c.votes - 1)
        };
      }
      return c;
    }));
  };

  // Add a reply to a comment
  const handleAddReply = (commentId: number) => {
    const text = newReplyTexts[commentId];
    if (!text || !text.trim()) return;

    const newReply = {
      id: Date.now(),
      author: 'João Silva',
      role: 'Ciência de Dados',
      photo: getMemberPhoto('João Silva'),
      text: text.trim(),
      time: 'Agora mesmo',
      votes: 0,
      likedByCurrentUser: false
    };

    setComments(prev => prev.map(c => {
      if (c.id === commentId) {
        return {
          ...c,
          replies: [...(c.replies || []), newReply]
        };
      }
      return c;
    }));

    setNewReplyTexts(prev => ({ ...prev, [commentId]: '' }));
    setActiveReplyBoxCommentId(null);
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await deleteCommentMutation.mutateAsync(String(commentId));
      showSuccess('Comentário removido.');
      refetchComments();
      refetchDashboard();
    } catch {
      showError('Erro ao remover comentário.');
    }
  };

  // Modals / Controllers
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddDocModalOpen, setIsAddDocModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  // Form states
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('Média');
  const [newTaskStatus, setNewTaskStatus] = useState('TODO');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskEstimateHours, setNewTaskEstimateHours] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState('');

  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocContent, setNewDocContent] = useState('');

  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('Developer');
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  const [newMilestoneName, setNewMilestoneName] = useState('');
  const [newMilestoneDueDate, setNewMilestoneDueDate] = useState('');
  const [isAddingSprint, setIsAddingSprint] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');
  const [newSprintGoal, setNewSprintGoal] = useState('');
  const [newSprintStartDate, setNewSprintStartDate] = useState('');
  const [newSprintEndDate, setNewSprintEndDate] = useState('');

  // Edit project state
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectStatus, setEditProjectStatus] = useState('');
  const [editProjectPriority, setEditProjectPriority] = useState('');
  const [editProjectBudget, setEditProjectBudget] = useState(0);
  const [editProjectProgress, setEditProjectProgress] = useState(0);

  useEffect(() => {
    if (project) {
      setEditProjectName(project.name || '');
      setEditProjectStatus(project.status || 'Planejamento');
      setEditProjectPriority(project.priority || 'Média');
      setEditProjectBudget(Number(project.budget || 0));
      setEditProjectProgress(Number(project.progress || 0));
    }
  }, [project]);

  // Atividades reais a partir do histórico (auditLogs) do dashboard
  const activities = useMemo(() => {
    return (dashboard?.history || []).map((h: any) => ({
      id: h.id,
      text: h.action,
      type: 'system',
      time: h.time ? new Date(h.time).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—',
    }));
  }, [dashboard]);

  // Arquivos reais a partir dos documentos do dashboard
  const [files, setFiles] = useState<any[]>([]);
  useEffect(() => {
    if (dashboard?.documents) {
      setFiles(dashboard.documents.map((d: any) => ({
        name: d.filename,
        size: d.size || '—',
        type: (d.mime || 'Arquivo').split('/').pop()?.toUpperCase() || 'Arquivo',
        uploader: d.uploadedBy || 'Sistema',
        date: d.createdAt ? new Date(d.createdAt).toLocaleDateString('pt-BR') : '—',
      })));
    }
  }, [dashboard]);

  // Local action triggers
  const handleSaveProjectInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProjectName.trim()) return;

    try {
      await updateProjectMutation.mutateAsync({
        id: projectId,
        data: {
          name: editProjectName,
          status: editProjectStatus,
          priority: editProjectPriority,
          budget: editProjectBudget,
          progress: editProjectProgress
        }
      });
      showSuccess('Projeto atualizado com sucesso!');
      setIsEditModalOpen(false);
      // A mutation invalida automaticamente as queries do dashboard (ver useProjects)
      refetchDashboard();
    } catch {
      showError('Ocorreu um erro ao atualizar os parâmetros do projeto.');
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    if (!projectId) {
      showError('Não foi possível identificar o projeto. Atualize a página e tente novamente.');
      return;
    }

    try {
      const metadata: any = {};
      if (newTaskAssignee.trim()) metadata.assignee = newTaskAssignee.trim();
      if (newTaskCategory.trim()) metadata.category = newTaskCategory.trim();

      const payload: any = {
        projectId,
        title: newTaskTitle,
        status: newTaskStatus,
        priority: newTaskPriority,
        description: newTaskDescription.trim() || undefined,
        dueDate: newTaskDueDate || undefined,
        estimateHours: newTaskEstimateHours ? Number(newTaskEstimateHours) : undefined,
        metadata: Object.keys(metadata).length ? metadata : undefined,
      };

      const { data } = await apiClient.post(`/api/projects/${projectId}/tasks`, payload);

      showSuccess('Tarefa criada com sucesso!');
      setNewTaskTitle('');
      setNewTaskDescription('');
      setNewTaskDueDate('');
      setNewTaskEstimateHours('');
      setNewTaskAssignee('');
      setNewTaskCategory('');
        setNewTaskPriority('Média');
        setNewTaskStatus('TODO');
        setIsAddTaskModalOpen(false);
        refetchDashboard();
      } catch {
        showError('Erro ao registrar tarefa.');
      }
    };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await apiClient.delete(`/api/tasks/${taskId}`);
      showSuccess('Tarefa removida!');
      refetchDashboard();
    } catch {
      showError('Erro ao deletar tarefa.');
    }
  };

  const handleUpdateTaskStatus = async (taskId: string | number, newStatus: string) => {
    try {
      await apiClient.put(`/api/tasks/${taskId}`, { status: newStatus });
      refetchDashboard();
    } catch {
      showError('Falha ao atualizar status da tarefa.');
    }
  };

  const handleCreateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMilestoneName.trim()) return;

    try {
      await apiClient.post(`/api/projects/${projectId}/milestones`, {
        name: newMilestoneName.trim(),
        dueDate: newMilestoneDueDate || undefined,
      });
      setNewMilestoneName('');
      setNewMilestoneDueDate('');
      setIsAddingMilestone(false);
      await Promise.all([refetchMilestones(), refetchDashboard()]);
      showSuccess('Marco criado com sucesso!');
    } catch {
      showError('Erro ao criar marco.');
    }
  };

  const handleToggleMilestone = async (milestone: any) => {
    try {
      await apiClient.put(`/api/milestones/${milestone.id}`, {
        completedAt: milestone.completedAt ? null : new Date().toISOString(),
      });
      await Promise.all([refetchMilestones(), refetchDashboard()]);
    } catch {
      showError('Erro ao atualizar marco.');
    }
  };

  const handleDeleteMilestone = async (milestoneId: string) => {
    try {
      await apiClient.delete(`/api/milestones/${milestoneId}`);
      await Promise.all([refetchMilestones(), refetchDashboard()]);
      showSuccess('Marco removido.');
    } catch {
      showError('Erro ao remover marco.');
    }
  };

  const handleCreateSprint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSprintName.trim()) return;

    try {
      await apiClient.post(`/api/projects/${projectId}/sprints`, {
        name: newSprintName.trim(),
        goal: newSprintGoal.trim() || undefined,
        startDate: newSprintStartDate || undefined,
        endDate: newSprintEndDate || undefined,
        status: 'PLANNED',
      });
      setNewSprintName('');
      setNewSprintGoal('');
      setNewSprintStartDate('');
      setNewSprintEndDate('');
      setIsAddingSprint(false);
      await Promise.all([refetchSprints(), refetchDashboard()]);
      showSuccess('Sprint criada com sucesso!');
    } catch {
      showError('Erro ao criar sprint.');
    }
  };

  const handleUpdateSprintStatus = async (sprint: any, status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED') => {
    try {
      await apiClient.put(`/api/projects/${projectId}/sprints/${sprint.id}`, { status });
      await Promise.all([refetchSprints(), refetchDashboard()]);
    } catch {
      showError('Erro ao atualizar sprint.');
    }
  };

  const addMemberMutation = useCreateProjectMember(projectId);
  const deleteMemberMutation = useDeleteProjectMember(projectId);
  const createDocumentMutation = useCreateDocument();

  const handleCreateDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocTitle.trim()) return;

    try {
      await createDocumentMutation.mutateAsync({
        filename: newDocTitle,
        title: newDocTitle,
        content: newDocContent || 'Documentação inicial sem notas adicionais.',
        folder: 'Geral',
        type: 'DOC',
        resourceType: 'project',
        resourceId: projectId,
        status: 'ACTIVE',
      });

      showSuccess('Documento técnico publicado!');
      setNewDocTitle('');
      setNewDocContent('');
      setIsAddDocModalOpen(false);
      refetchDashboard();
    } catch {
      showError('Erro ao publicar documento.');
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemberName.trim()) return;

    try {
      await addMemberMutation.mutateAsync({
        userId: 'current-user', // Will be resolved by backend from token
        role: newMemberRole,
        allocation: 100,
      });
      showSuccess(`Membro ${newMemberName} adicionado ao projeto!`);
      setNewMemberName('');
      setIsAddMemberModalOpen(false);
      refetchDashboard();
    } catch {
      showError('Erro ao adicionar membro.');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      await deleteMemberMutation.mutateAsync(memberId);
      showSuccess('Membro removido do projeto.');
      refetchDashboard();
    } catch {
      showError('Erro ao remover membro.');
    }
  };

  const handleDropFile = (e: React.DragEvent) => {
    e.preventDefault();
    showError('O upload de arquivos ainda não está disponível no backend deste workspace.');
  };

  if (!project) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-neutral-400 mb-4" size={32} />
        <p className="text-sm text-neutral-500 font-bold">Carregando os dados operacionais do projeto...</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-8 animate-in fade-in duration-500 relative px-2 sm:px-4 md:px-8 text-left">
      
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          BREADCRUMBS & TOP BAR
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-3 text-xs font-bold text-neutral-400">
        <button 
          onClick={() => setCurrentView('projetos')}
          className="hover:text-neutral-900 flex items-center gap-1 transition-colors"
        >
          Projetos
        </button>
        <ChevronRight size={10} />
        <span className="text-neutral-900 font-black">{project.name}</span>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          PREMIUM HERO HEADER
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.02)] flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          
          {/* Avatar and Main Info */}
          <div className="flex items-start gap-4 sm:gap-5">
            <button 
              onClick={() => {
                setGlobalFilters(prev => ({ ...prev, projectId: undefined }));
                setCurrentView('projetos');
              }}
              className="p-2.5 rounded-xl hover:bg-neutral-50 text-neutral-400 hover:text-neutral-900 transition-all border border-neutral-100/50 mr-1 mt-1 cursor-pointer"
              title="Voltar para a Lista de Projetos"
            >
              <ArrowLeft size={16} strokeWidth={2.5} />
            </button>
            
            <div className="w-14 h-14 rounded-2xl bg-neutral-900 text-white border border-neutral-800 flex items-center justify-center font-black text-xl shadow-inner">
              {project.name ? project.name.substring(0, 2).toUpperCase() : 'CY'}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight">{project.name}</h1>
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 border border-emerald-100/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  🟢 Saudável
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 text-xs font-semibold text-[#64748B]">
                {projectCompany && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase size={13} className="text-neutral-400" />
                    <span className="text-[#0F172A] font-bold">{projectCompany.name}</span>
                  </span>
                )}
                <span className="text-neutral-300">•</span>
                <span className="flex items-center gap-1.5">
                  <User size={13} className="text-neutral-400" />
                  <span>Responsável: <strong className="text-neutral-800">{project.owner || 'Não definido'}</strong></span>
                </span>
                <span className="text-neutral-300">•</span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-neutral-400" />
                  <span>Prazo: {project.dueDate || 'Não definido'}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center gap-2.5 sm:self-end lg:self-center">
            <button 
              onClick={syncWorkspaceData}
              className="p-2 bg-white text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-xl transition-all border border-neutral-200 shadow-sm cursor-pointer"
              title="Atualizar dados"
            >
              <RefreshCw size={14} />
            </button>
            <button 
              onClick={() => setIsEditModalOpen(true)}
              className="px-4 py-2 text-xs font-bold bg-white text-[#0F172A] hover:bg-neutral-50 rounded-xl transition-all border border-neutral-200 shadow-sm cursor-pointer flex items-center gap-1.5"
            >
              <Edit3 size={13} />
              <span>Editar projeto</span>
            </button>
            <button 
              onClick={() => setActiveTab('configuracoes')}
              className="px-4 py-2 text-xs font-bold bg-neutral-900 text-white hover:bg-black rounded-xl transition-all shadow-sm cursor-pointer flex items-center gap-1.5"
            >
              <Settings size={13} />
              <span>Mais opções</span>
            </button>
          </div>
        </div>

        {/* Premium Meta Indicator Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-[#0F172A0A] pt-6">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Progresso Geral</span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-black text-neutral-900">{project.progress || 0}%</span>
              <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden max-w-[80px]">
                <div className="bg-neutral-900 h-full rounded-full" style={{ width: `${project.progress || 0}%` }}></div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Prioridade</span>
            <span className="text-sm font-bold text-neutral-800 flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${project.priority === 'Alta' ? 'bg-rose-500' : 'bg-blue-500'}`} />
              {project.priority || 'Média'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Tarefas Concluídas</span>
            <span className="text-sm font-bold text-neutral-800">
              {projectTasks.filter(t => t.status === 'DONE' || t.status === 'CONCLUIDO').length} de {projectTasks.length}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Faturamento Alocado</span>
            <span className="text-sm font-black text-emerald-600">
              R$ {Number(project.budget || 0).toLocaleString('pt-BR')}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[#0F172A08]" />

        {/* Integrated Horizontal Navigation Tabs Menu */}
        <div className="flex flex-col gap-2">
          <div className="w-full -mx-4">
            <ScrollableTabsContainer activeId={activeTab} className="pb-2">
              {PROJECT_WORKSPACE_GROUPS.map((group) => (
                <div key={group.label} className="flex items-center gap-1.5 shrink-0 px-1 first:pl-0">
                  <div className="flex items-center gap-1.5">
                    {group.tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isSelected = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          data-selected={isSelected}
                          onClick={() => setActiveTab(tab.id)}
                          className={`group flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                            isSelected
                              ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-2xs'
                              : 'bg-[#FAFAFA] text-slate-600 border-[#0F172A08] hover:bg-slate-100/90 hover:text-slate-900 hover:border-[#0F172A15]'
                          }`}
                        >
                          <Icon
                            size={13}
                            className={isSelected ? 'text-indigo-400' : 'text-slate-400 group-hover:text-slate-600'}
                          />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </ScrollableTabsContainer>
          </div>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          TAB RENDERING WINDOWS
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full flex flex-col gap-6"
          >
            
            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                1. TAB: RESUMO (DASHBOARD)
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'resumo' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {[
                    ['Progresso', `${project.progress || 0}%`],
                    ['Status', project.status || 'Não informado'],
                    ['Prazo', project.dueDate || 'Não definido'],
                    ['Concluídas', `${commandCenter.completed}/${projectTasks.length}`],
                    ['Orçamento', commandCenter.budget > 0 ? `R$ ${commandCenter.budget.toLocaleString('pt-BR')}` : 'Não informado'],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-white border border-neutral-100 p-5 rounded-2xl flex flex-col gap-2">
                      <span className="text-[10px] font-black uppercase text-neutral-400">{label}</span>
                      <h3 className="text-xl font-black text-neutral-900">{value}</h3>
                      {label === 'Progresso' && <div className="w-full bg-neutral-100 h-1.5 rounded-full overflow-hidden"><div className="bg-neutral-900 h-full rounded-full" style={{ width: `${project.progress || 0}%` }} /></div>}
                    </div>
                  ))}
                </div>

                <div className="lg:col-span-2 flex flex-col gap-6">
                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Atenção necessária</h3><AlertTriangle size={16} className="text-neutral-400" /></div>
                    {[
                      { label: 'Tarefas atrasadas', count: commandCenter.overdueTasks.length, action: () => { setTaskStatusFilter(''); setActiveTab('tarefas'); } },
                      { label: 'Tarefas vencendo hoje', count: commandCenter.dueTodayTasks.length, action: () => { setTaskSearch(''); setActiveTab('tarefas'); } },
                      { label: 'Tarefas sem responsável', count: commandCenter.unassignedTasks.length, action: () => { setTaskAssigneeFilter('Não atribuído'); setActiveTab('tarefas'); } },
                      { label: 'Bloqueios', count: commandCenter.blockedTasks.length, action: () => { setTaskStatusFilter('BLOCKED'); setActiveTab('tarefas'); } },
                      ...(commandCenter.financialAlert ? [{ label: 'Orçamento acima do gasto', count: 1, action: () => setActiveTab('financeiro') }] : []),
                    ].map((item) => (
                      <button key={item.label} onClick={item.action} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-neutral-100 hover:border-neutral-300 hover:bg-neutral-50 text-left cursor-pointer">
                        <span className="text-xs font-bold text-neutral-700">{item.label}</span><span className="text-xs font-black text-neutral-900">{item.count}</span>
                      </button>
                    ))}
                    {commandCenter.overdueTasks.length === 0 && commandCenter.dueTodayTasks.length === 0 && commandCenter.unassignedTasks.length === 0 && commandCenter.blockedTasks.length === 0 && !commandCenter.financialAlert && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-3 font-semibold">O projeto está em dia. Nenhuma ação crítica necessária.</p>}
                  </div>

                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Execução atual</h3><button onClick={() => setActiveTab('kanban')} className="text-[10px] font-bold text-neutral-500 hover:text-neutral-900">Abrir Board</button></div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[['Backlog', commandCenter.execution.todo], ['Em andamento', commandCenter.execution.inProgress], ['Review', commandCenter.execution.review], ['Concluído', commandCenter.execution.done]].map(([label, count]) => <button key={label} onClick={() => { setTaskStatusFilter(label === 'Backlog' ? 'TODO' : label === 'Em andamento' ? 'IN_PROGRESS' : label === 'Review' ? 'REVIEW' : 'DONE'); setActiveTab('tarefas'); }} className="border border-neutral-100 rounded-xl p-3 text-left hover:bg-neutral-50 cursor-pointer"><span className="text-[10px] font-bold text-neutral-500">{label}</span><strong className="block text-2xl font-black text-neutral-900 mt-1">{count}</strong></button>)}
                    </div>
                    {commandCenter.activeSprint && <button onClick={() => setActiveTab('sprints')} className="text-left border border-neutral-100 rounded-xl p-3 hover:bg-neutral-50 cursor-pointer"><span className="text-[10px] font-bold uppercase text-neutral-400">Sprint atual</span><strong className="block text-sm font-black text-neutral-900 mt-1">{commandCenter.activeSprint.name}</strong><span className="text-[10px] text-neutral-500">{commandCenter.activeSprint.startDate ? new Date(commandCenter.activeSprint.startDate).toLocaleDateString('pt-BR') : 'Sem início'} → {commandCenter.activeSprint.endDate ? new Date(commandCenter.activeSprint.endDate).toLocaleDateString('pt-BR') : 'Sem fim'}</span></button>}
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4"><div className="flex justify-between items-center"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Minha fila</h3><button onClick={() => setActiveTab('tarefas')} className="text-[10px] font-bold text-neutral-500">Ver lista</button></div>{commandCenter.myTasks.length > 0 ? commandCenter.myTasks.slice(0, 5).map((task: any) => <button key={task.id} onClick={() => { setTaskSearch(task.title || ''); setActiveTab('tarefas'); }} className="text-left border-b border-neutral-50 pb-3 last:border-0 hover:text-neutral-900 cursor-pointer"><span className="text-xs font-bold text-neutral-800 block">{task.title}</span><span className="text-[10px] text-neutral-500">{task.priority || 'Prioridade não definida'} · {task.dueDate ? formatDueStatus(task.dueDate) : 'Sem prazo'} · {task.status}</span></button>) : <p className="text-xs text-neutral-400 font-semibold">Nenhuma tarefa atribuída ao usuário atual.</p>}</div>
                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Próximos prazos</h3>{commandCenter.upcoming.length > 0 ? commandCenter.upcoming.map((item: any) => <button key={item.id} onClick={item.action} className="text-left flex justify-between gap-3 border-b border-neutral-50 pb-3 last:border-0 hover:text-neutral-900 cursor-pointer"><span><span className="text-[10px] font-bold uppercase text-neutral-400 block">{item.kind}</span><span className="text-xs font-bold text-neutral-800">{item.label}</span></span><span className="text-[10px] font-bold text-neutral-500 whitespace-nowrap">{new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span></button>) : <p className="text-xs text-neutral-400 font-semibold">Nenhum prazo futuro encontrado.</p>}</div>
                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4"><div className="flex justify-between items-center"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Próximos marcos</h3><button onClick={() => setActiveTab('roadmap')} className="text-[10px] font-bold text-neutral-500">Abrir roadmap</button></div>{projectMilestones.length > 0 ? projectMilestones.slice(0, 4).map((milestone: any) => { const progress = milestone.completedAt ? 100 : 0; return <button key={milestone.id} onClick={() => setActiveTab('roadmap')} className="text-left border-b border-neutral-50 pb-3 last:border-0 hover:text-neutral-900 cursor-pointer"><div className="flex justify-between gap-3"><span className="text-xs font-bold text-neutral-800">{milestone.name}</span><span className="text-[10px] font-black text-neutral-500">{progress}%</span></div><div className="mt-2 w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-neutral-900 rounded-full" style={{ width: `${progress}%` }} /></div></button>; }) : <p className="text-xs text-neutral-400 font-semibold">Nenhum marco cadastrado.</p>}</div>
                </div>

                <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4"><div className="flex items-center justify-between"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Atividade recente</h3><button onClick={() => setActiveTab('discussions')} className="text-[10px] font-bold text-neutral-500">Ver atividade</button></div>{activities.length > 0 ? activities.slice(0, 6).map((activity: any) => <div key={activity.id} className="flex gap-3 border-b border-neutral-50 pb-3 last:border-0"><span className="w-1.5 h-1.5 rounded-full bg-neutral-900 mt-1.5 shrink-0" /><div><p className="text-xs font-bold text-neutral-800">{activity.text}</p><span className="text-[10px] text-neutral-400 font-semibold">{activity.time}</span></div></div>) : <p className="text-xs text-neutral-400 font-semibold">Nenhuma atividade registrada.</p>}</div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Ações rápidas</h3><div className="grid grid-cols-2 gap-2"><button onClick={() => setIsAddTaskModalOpen(true)} className="px-3 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold">Nova tarefa</button><button onClick={() => { setActiveTab('roadmap'); setIsAddingMilestone(true); }} className="px-3 py-2 border border-neutral-200 rounded-xl text-xs font-bold">Novo marco</button><button onClick={() => { setActiveTab('sprints'); setIsAddingSprint(true); }} className="px-3 py-2 border border-neutral-200 rounded-xl text-xs font-bold">Nova sprint</button><button onClick={() => setIsAddMemberModalOpen(true)} className="px-3 py-2 border border-neutral-200 rounded-xl text-xs font-bold">Adicionar membro</button><button onClick={() => setIsAddDocModalOpen(true)} className="col-span-2 px-3 py-2 border border-neutral-200 rounded-xl text-xs font-bold">Novo documento</button></div></div>

                {projectFinance.length > 0 && <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-3"><h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Alertas financeiros</h3><span className="text-xs text-neutral-500">Despesas registradas: <strong className="text-neutral-900">R$ {projectCostTotal.toLocaleString('pt-BR')}</strong></span><span className="text-xs text-neutral-500">Receitas registradas: <strong className="text-emerald-700">R$ {commandCenter.revenueTotal.toLocaleString('pt-BR')}</strong></span><span className={`text-xs font-bold ${commandCenter.financialAlert ? 'text-rose-700' : 'text-emerald-700'}`}>{commandCenter.financialAlert ? 'Gasto acima do orçamento' : 'Sem alerta de orçamento'}</span></div>}
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                2. TAB: KANBAN OPERACIONAL DO PROJETO
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'kanban' && (
              <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-neutral-100 shadow-xs">
                    <span className="text-xs font-bold text-neutral-500">Organize o fluxo operacional técnico</span>
                    <button
                      onClick={() => setIsAddTaskModalOpen(true)}
                      className="px-3.5 py-1.5 bg-neutral-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black transition-all cursor-pointer"
                    >
                      <Plus size={14} /> Adicionar Tarefa
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                      { id: 'TODO', label: 'Backlog', color: 'border-slate-200 bg-slate-50/50' },
                      { id: 'IN_PROGRESS', label: 'Em Andamento', color: 'border-blue-100 bg-blue-50/10' },
                      { id: 'REVIEW', label: 'Review / Teste', color: 'border-amber-100 bg-amber-50/10' },
                      { id: 'DONE', label: 'Concluído', color: 'border-emerald-100 bg-emerald-50/10' }
                    ].map((col) => {
                      const colTasks = projectTasks.filter(t => t.status === col.id || (col.id === 'TODO' && t.status === 'backlog') || (col.id === 'IN_PROGRESS' && t.status === 'desenvolvimento') || (col.id === 'DONE' && t.status === 'concluido'));

                      return (
                        <DroppableKanbanColumn key={col.id} column={col} tasks={colTasks} onUpdateStatus={handleUpdateTaskStatus} onDeleteTask={handleDeleteTask} activeDragTaskId={activeDragTaskId} />
                      );
                    })}
                  </div>
                </div>
                <DragOverlay>
                  {activeDragTaskId ? (
                    <div className="bg-white border-2 border-indigo-500 p-4 rounded-3xl shadow-xl opacity-80 rotate-3 scale-105">
                      <span className="text-xs font-black uppercase tracking-[0.24em] text-neutral-500">Movendo tarefa...</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                3. TAB: ROADMAP DO PROJETO
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'roadmap' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-base font-black text-neutral-900">Roadmap Estratégico</h3>
                    <p className="text-xs text-neutral-500 font-bold">Marcos operacionais e etapas do escopo de entrega.</p>
                  </div>
                  <button onClick={() => setIsAddingMilestone((current) => !current)} className="px-3 py-1.5 bg-neutral-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black transition-all cursor-pointer">
                    <Plus size={14} /> Adicionar marco
                  </button>
                </div>

                {isAddingMilestone && (
                  <form onSubmit={handleCreateMilestone} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3 p-4 rounded-xl border border-neutral-100 bg-neutral-50/60">
                    <input value={newMilestoneName} onChange={(e) => setNewMilestoneName(e.target.value)} placeholder="Nome do marco" required className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                    <input type="date" value={newMilestoneDueDate} onChange={(e) => setNewMilestoneDueDate(e.target.value)} className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                    <button type="submit" className="px-3 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold">Salvar</button>
                  </form>
                )}

                <div className="flex flex-col gap-6 relative before:absolute before:left-3.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-neutral-100">
                  {(apiMilestones?.data || []).length > 0 ? (
                    (apiMilestones?.data || []).map((milestone: any) => {
                      const isCompleted = !!milestone.completedAt;
                      const isOverdue = milestone.dueDate && new Date(milestone.dueDate) < new Date() && !isCompleted;
                      const status = isCompleted ? 'concluido' : isOverdue ? 'atrasado' : 'andamento';

                      return (
                        <div key={milestone.id} className="flex gap-6 relative pl-8">
                          <div className={`absolute left-1.5 top-1.5 w-4 h-4 rounded-full border-4 border-white flex items-center justify-center ${
                            status === 'concluido' ? 'bg-neutral-950' : status === 'atrasado' ? 'bg-rose-500' : 'bg-blue-500'
                          }`} />

                          <div className="flex-1 border border-neutral-100 p-4 rounded-xl hover:border-neutral-200 transition-colors">
                            <div className="flex justify-between items-start gap-4">
                              <div>
                                <h4 className="text-xs font-black text-neutral-900">{milestone.name}</h4>
                                <span className="text-[10px] text-neutral-400 font-bold">
                                  {milestone.dueDate ? new Date(milestone.dueDate).toLocaleDateString('pt-BR') : 'Sem data'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                              <button onClick={() => handleToggleMilestone(milestone)} className={`text-[9px] font-bold px-2 py-0.5 rounded-full cursor-pointer ${
                                status === 'concluido' ? 'bg-emerald-50 text-emerald-700' :
                                status === 'atrasado' ? 'bg-rose-50 text-rose-700' :
                                'bg-blue-50 text-blue-700'
                              }`}>
                                {status === 'concluido' ? 'Entregue' : status === 'atrasado' ? 'Atrasado' : 'Em Progresso'}
                              </button>
                              <button onClick={() => handleDeleteMilestone(milestone.id)} className="text-neutral-400 hover:text-rose-600 cursor-pointer" title="Remover marco"><Trash2 size={13} /></button>
                              </div>
                            </div>
                            {milestone.completedAt && (
                              <p className="text-[10px] text-neutral-400 mt-1">
                                Concluído em {new Date(milestone.completedAt).toLocaleDateString('pt-BR')}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs text-neutral-400 font-semibold">Nenhum marco cadastrado para este projeto.</p>
                  )}
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                4. TAB: SPRINTS
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'sprints' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                  <div className="flex justify-between items-center border-b border-neutral-50 pb-4">
                    <div>
                      <span className="text-[10px] font-black text-neutral-400 uppercase tracking-widest">Sprints do Projeto</span>
                      <h3 className="text-base font-black text-neutral-900">
                        {(apiSprints?.data || []).length > 0
                          ? `${(apiSprints?.data || [])[0].name}`
                          : 'Nenhuma sprint ativa'}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-neutral-500">{(apiSprints?.data || []).length > 0 ? `${(apiSprints?.data || []).length} sprint(s)` : '—'}</span>
                      <button onClick={() => setIsAddingSprint((current) => !current)} className="px-3 py-1.5 bg-neutral-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black transition-all cursor-pointer"><Plus size={13} /> Nova sprint</button>
                    </div>
                  </div>

                  {isAddingSprint && (
                    <form onSubmit={handleCreateSprint} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-xl border border-neutral-100 bg-neutral-50/60">
                      <input value={newSprintName} onChange={(e) => setNewSprintName(e.target.value)} placeholder="Nome da sprint" required className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                      <input value={newSprintGoal} onChange={(e) => setNewSprintGoal(e.target.value)} placeholder="Objetivo da sprint" className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                      <input type="date" value={newSprintStartDate} onChange={(e) => setNewSprintStartDate(e.target.value)} className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                      <input type="date" value={newSprintEndDate} onChange={(e) => setNewSprintEndDate(e.target.value)} className="bg-white border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold outline-none" />
                      <div className="md:col-span-2 flex justify-end"><button type="submit" className="px-3 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold">Salvar sprint</button></div>
                    </form>
                  )}

                  <div className="flex flex-col gap-4">
                    <h4 className="text-xs font-black text-neutral-800 uppercase tracking-wider">Entregas Planejadas</h4>
                    {(apiSprints?.data || []).length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {(apiSprints?.data || []).map((sprint: any) => (
                          <div key={sprint.id} className="p-3 border border-neutral-100 rounded-xl flex justify-between items-center">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold text-neutral-800">{sprint.name}</span>
                              {sprint.goal && (
                                <span className="text-[10px] text-neutral-400 font-semibold">{sprint.goal}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                sprint.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' :
                                sprint.status === 'PLANNED' ? 'bg-blue-50 text-blue-700' :
                                sprint.status === 'COMPLETED' ? 'bg-neutral-100 text-neutral-600' :
                                'bg-rose-50 text-rose-700'
                              }`}>
                                {sprint.status}
                              </span>
                              {sprint.status === 'PLANNED' && <button onClick={() => handleUpdateSprintStatus(sprint, 'ACTIVE')} className="text-[10px] font-bold text-blue-700 hover:underline">Iniciar</button>}
                              {sprint.status === 'ACTIVE' && <button onClick={() => handleUpdateSprintStatus(sprint, 'COMPLETED')} className="text-[10px] font-bold text-emerald-700 hover:underline">Encerrar</button>}
                              {sprint.status !== 'COMPLETED' && sprint.status !== 'CANCELLED' && <button onClick={() => handleUpdateSprintStatus(sprint, 'CANCELLED')} className="text-[10px] font-bold text-rose-600 hover:underline">Cancelar</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-neutral-400 font-semibold">Nenhuma sprint cadastrada para este projeto.</p>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4">
                  <h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Informações</h3>
                  {(apiSprints?.data || []).length > 0 ? (
                    <div className="flex flex-col gap-3">
                      <div className="border border-neutral-100 p-4 rounded-xl flex flex-col gap-2">
                        <span className="text-xs text-neutral-500">Total de Sprints</span>
                        <h4 className="text-2xl font-black text-neutral-900">{(apiSprints?.data || []).length}</h4>
                      </div>
                      <div className="border border-neutral-100 p-4 rounded-xl flex flex-col gap-2">
                        <span className="text-xs text-neutral-500">Status</span>
                        <h4 className="text-xl font-bold text-neutral-800">
                          {(apiSprints?.data || []).some((s: any) => s.status === 'ACTIVE') ? 'Ativa' : 'Nenhuma ativa'}
                        </h4>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-neutral-400 font-semibold">Crie sprints para visualizar métricas.</p>
                  )}
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                5. TAB: EQUIPE / SQUAD
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'equipe' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-black text-neutral-900">Time Alocado</h3>
                    <p className="text-xs text-neutral-500 font-bold">Squad responsável pela execução técnica.</p>
                  </div>
                  <button
                    onClick={() => setIsAddMemberModalOpen(true)}
                    className="px-3 py-1.5 bg-neutral-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black transition-all cursor-pointer"
                  >
                    <Plus size={14} /> Convidar Membro
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {projectMembers.map((memb) => (
                    <div key={memb.id} className="border border-neutral-100 p-4 rounded-2xl flex flex-col items-center text-center gap-3 relative group">
                      <div className="w-12 h-12 rounded-full bg-neutral-100 text-neutral-800 flex items-center justify-center font-black text-sm border border-neutral-200 shadow-inner overflow-hidden">
                        {memb.avatarUrl ? (
                          <img src={memb.avatarUrl} alt={memb.name} className="w-full h-full object-cover" />
                        ) : (
                          (memb.name || '?').substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-neutral-900">{memb.name || 'Membro'}</h4>
                        <span className="text-[10px] text-neutral-400 font-bold block mt-0.5">{memb.role || '—'}</span>
                      </div>
                      <span className="text-[10px] font-bold text-neutral-500 bg-neutral-50 px-2.5 py-1 rounded-md border border-neutral-100">{memb.email || '—'}</span>
                      <button
                        onClick={() => handleRemoveMember(memb.id)}
                        className="absolute top-2 right-2 p-1 rounded-lg bg-white border border-neutral-200 text-neutral-400 hover:text-rose-600 hover:border-rose-200 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                        title="Remover membro"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}

                  {projectMembers.length === 0 && (
                    <div className="lg:col-span-4 py-8 text-center text-xs text-neutral-400 font-bold">
                      Nenhum membro alocado a este projeto.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                5.5 TAB: TIMELINE DISCUSSIONS (SOCIAL FEED CONCEPT)
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'discussions' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
                
                {/* Left side: Timeline Social Feed */}
                <div className="lg:col-span-2 flex flex-col gap-5">
                  
                  {/* Search and Filters Container */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4">
                    {/* Search Bar */}
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Pesquisar posts, decisões, arquivos, tags, comentários..."
                        className="w-full pl-10 pr-4 py-3 bg-neutral-50 hover:bg-neutral-100/50 focus:bg-white border border-neutral-200 focus:border-indigo-500 rounded-2xl text-xs sm:text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 font-semibold transition-all"
                      />
                      <div className="absolute left-3.5 top-3.5 text-neutral-400">
                        <Search size={16} />
                      </div>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3.5 top-3.5 text-neutral-400 hover:text-neutral-600 text-xs font-bold"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* Feed Filters Bar */}
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] font-black uppercase text-neutral-400 tracking-wider mr-2">Filtrar Atividades:</span>
                      {[
                        { id: 'all', label: 'Tudo', count: comments.length },
                        { id: 'decision', label: '⚡ Decisões', count: comments.filter(c => c.type === 'decision').length },
                        { id: 'suggestion', label: '💡 Sugestões', count: comments.filter(c => c.type === 'idea').length },
                        { id: 'document', label: '📄 Documentos', count: comments.filter(c => c.type === 'document' || c.type === 'image').length },
                        { id: 'comment', label: '💬 Comentários', count: comments.filter(c => c.type === 'comment').length },
                        { id: 'event', label: '📅 Eventos/Deploys', count: comments.filter(c => c.type === 'event' || c.type === 'meeting' || c.type === 'deploy').length },
                        { id: 'system', label: '⚙️ Sistema', count: comments.filter(c => c.type === 'system').length },
                        { id: 'ia', label: '🤖 IA Neural', count: comments.filter(c => c.type === 'ai').length },
                        { id: 'approval', label: '✅ Aprovações', count: comments.filter(c => c.type === 'approval' || c.type === 'rejection').length }
                      ].map(f => (
                        <button
                          key={f.id}
                          onClick={() => setDiscussionFilterType(f.id)}
                          className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                            discussionFilterType === f.id
                              ? 'bg-indigo-600 text-white shadow-xs'
                              : 'bg-neutral-50 hover:bg-neutral-100 text-neutral-600 border border-neutral-100'
                          }`}
                        >
                          <span>{f.label}</span>
                          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                            discussionFilterType === f.id ? 'bg-indigo-700 text-indigo-100' : 'bg-neutral-200/80 text-neutral-500'
                          }`}>
                            {f.count}
                          </span>
                        </button>
                      ))}
                    </div>

                    {discussionFilterTag && (
                      <div className="flex">
                        <button
                          onClick={() => setDiscussionFilterTag(null)}
                          className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[10px] font-extrabold rounded-lg border border-indigo-100 flex items-center gap-1.5 cursor-pointer"
                        >
                          <span>Filtro de Tag: {discussionFilterTag}</span>
                          <span className="font-bold">✕</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* LinkedIn-style Post Creator card */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4">
                    <div className="flex gap-3 items-start">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 font-black text-sm flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                        {getMemberPhoto('João Silva') ? (
                          <img src={getMemberPhoto('João Silva')!} alt="JS" className="w-full h-full object-cover" />
                        ) : (
                          'JS'
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-3">
                        <textarea
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          placeholder="Publique uma atualização importante no feed corporativo. Use #hashtags para indexar..."
                          rows={3}
                          className="w-full p-3.5 bg-neutral-50 hover:bg-neutral-100/50 focus:bg-white border border-neutral-200 focus:border-indigo-500 rounded-2xl text-xs sm:text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 font-semibold transition-all resize-none"
                        />

                        {/* Interactive Hashtag Selector */}
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-black text-neutral-400 uppercase tracking-wider mr-1">Adicionar Hashtag:</span>
                          {['#engenharia', '#produto', '#mvp', '#arquitetura', '#validação', '#negócios', '#diretoria', '#infraestrutura', '#ia'].map(tag => {
                            const isSelected = selectedNewCommentTags.includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedNewCommentTags(prev => prev.filter(t => t !== tag));
                                  } else {
                                    setSelectedNewCommentTags(prev => [...prev, tag]);
                                  }
                                }}
                                className={`px-2 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer border ${
                                  isSelected
                                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-extrabold'
                                    : 'bg-neutral-50 hover:bg-neutral-100 border-neutral-200/60 text-neutral-500'
                                }`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-neutral-100 pt-3 flex flex-wrap gap-3 items-center justify-between">
                      {/* Post Category Picker */}
                      <div className="flex gap-1.5">
                        {[
                          { id: 'comment', label: 'Comentar', icon: '💬', color: 'hover:bg-neutral-100 hover:text-neutral-700 text-neutral-500 border-neutral-200' },
                          { id: 'idea', label: 'Sugerir 💡', icon: '💡', color: 'hover:bg-amber-50 hover:text-amber-700 text-neutral-500 border-neutral-200' },
                          { id: 'decision', label: 'Decidir ⚡', icon: '⚡', color: 'hover:bg-purple-50 hover:text-purple-700 text-neutral-500 border-neutral-200' }
                        ].map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setCommentType(t.id as any)}
                            className={`px-3 py-1.5 text-[10px] font-extrabold uppercase rounded-xl border cursor-pointer transition-all flex items-center gap-1.5 ${
                              commentType === t.id
                                ? commentType === 'decision'
                                  ? 'bg-purple-600 text-white border-purple-600 shadow-xs'
                                  : commentType === 'idea'
                                  ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                  : 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                : `bg-neutral-50 ${t.color}`
                            }`}
                          >
                            <span>{t.label}</span>
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={handleAddComment}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white text-xs font-bold rounded-xl cursor-pointer shadow-sm transition-all flex items-center gap-1.5"
                      >
                        <Send size={12} />
                        <span>Publicar no Feed</span>
                      </button>
                    </div>
                  </div>

                  {/* Social Feed List grouped by Timeline */}
                  <div className="flex flex-col gap-6">
                    {['Hoje', 'Ontem', 'Esta Semana', 'Este Mês', 'Mais Antigos'].map((group) => {
                      const groupItems = filteredComments.filter(c => c.timelineGroup === group || (!c.timelineGroup && group === 'Hoje'));
                      if (groupItems.length === 0) return null;

                      return (
                        <div key={group} className="flex flex-col gap-3">
                          {/* Date Header Divider */}
                          <div className="flex items-center gap-2 px-1">
                            <span className="text-[10px] font-black uppercase tracking-widest text-neutral-400 bg-neutral-100 px-2.5 py-1 rounded-md border border-neutral-200/40">
                              {group}
                            </span>
                            <div className="h-[1px] bg-neutral-100 flex-1" />
                          </div>

                          <div className="flex flex-col gap-4">
                            <AnimatePresence mode="popLayout">
                              {groupItems.map((comment) => {
                                const isExpanded = activeReplyBoxCommentId === comment.id;
                                return (
                                  <motion.div
                                    key={comment.id}
                                    layoutId={`comment-${comment.id}`}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className={`bg-white p-5 rounded-2xl border transition-all ${
                                      comment.isPinned 
                                        ? 'border-purple-300 shadow-sm ring-2 ring-purple-100/50' 
                                        : 'border-neutral-100 shadow-xs hover:border-neutral-200/80'
                                    }`}
                                  >
                                    {/* Post Header */}
                                    <div className="flex justify-between items-start mb-3">
                                      <div className="flex gap-3 items-center">
                                        <div className="w-10 h-10 rounded-full bg-neutral-100 border border-neutral-200 flex items-center justify-center text-xs font-extrabold overflow-hidden shrink-0 shadow-xs relative">
                                          {comment.photo ? (
                                            <img src={comment.photo} alt={comment.author} className="w-full h-full object-cover" />
                                          ) : (
                                            comment.author.charAt(0)
                                          )}
                                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                                        </div>
                                        <div className="flex flex-col text-left">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-xs sm:text-sm font-black text-neutral-800 leading-tight">{comment.author}</span>
                                            {comment.isPinned && (
                                              <span className="text-[9px] bg-purple-100 text-purple-700 font-extrabold px-1.5 py-0.2 rounded-md flex items-center gap-0.5">
                                                📌 Fixado
                                              </span>
                                            )}
                                            <span className="text-[10px] text-neutral-400 font-semibold">• {comment.time}</span>
                                          </div>
                                          <span className="text-[10px] text-neutral-400 font-bold leading-none mt-0.5">{comment.role}</span>
                                        </div>
                                      </div>

                                      {/* Custom badge for activity type */}
                                      <div>
                                        {comment.type === 'decision' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-purple-50 text-purple-700 border border-purple-100 flex items-center gap-1 shadow-2xs">
                                            <span>⚡</span>
                                            <span>Decisão Executiva</span>
                                          </span>
                                        ) : comment.type === 'idea' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-amber-50 text-amber-700 border border-amber-100 flex items-center gap-1 shadow-2xs">
                                            <span>💡</span>
                                            <span>Sugestão</span>
                                          </span>
                                        ) : comment.type === 'deploy' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-slate-900 text-slate-100 border border-slate-800 flex items-center gap-1 shadow-2xs">
                                            <span>🚀</span>
                                            <span>Deploy</span>
                                          </span>
                                        ) : comment.type === 'document' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 flex items-center gap-1">
                                            <span>📄</span>
                                            <span>Documento</span>
                                          </span>
                                        ) : comment.type === 'image' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-1">
                                            <span>🖼</span>
                                            <span>Imagem</span>
                                          </span>
                                        ) : comment.type === 'ai' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center gap-1 animate-pulse">
                                            <span>🤖</span>
                                            <span>IA Insight</span>
                                          </span>
                                        ) : comment.type === 'system' ? (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-neutral-100 text-neutral-600 border border-neutral-200 flex items-center gap-1">
                                            <span>⚙️</span>
                                            <span>Sistema</span>
                                          </span>
                                        ) : (
                                          <span className="text-[9px] font-extrabold uppercase px-2.5 py-1 rounded-xl bg-neutral-50 text-neutral-500 border border-neutral-200/60 flex items-center gap-1">
                                            <span>💬</span>
                                            <span>Comentário</span>
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Post Body Text */}
                                    <div className="pl-1 mb-3 text-left">
                                      <p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed whitespace-pre-line">
                                        {comment.text}
                                      </p>

                                      {/* Tags rendering */}
                                      {comment.tags && comment.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-3">
                                          {comment.tags.map(t => (
                                            <button
                                              key={t}
                                              onClick={() => setDiscussionFilterTag(t === discussionFilterTag ? null : t)}
                                              className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all cursor-pointer border ${
                                                discussionFilterTag === t
                                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                                  : 'bg-indigo-50/50 hover:bg-indigo-50 text-indigo-600 border-indigo-100'
                                              }`}
                                            >
                                              {t}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    {/* DECISION EXTRA BLOCK */}
                                    {comment.type === 'decision' && (
                                      <div className="mb-4 bg-purple-50/50 rounded-2xl border border-purple-100 p-4 text-xs flex flex-col gap-2.5 text-left font-semibold text-neutral-700">
                                        <div className="flex items-center gap-1.5 font-black text-purple-900 text-xs border-b border-purple-100/60 pb-1.5">
                                          <span>⚡ Decisão Executiva Homologada</span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                                          <div><span className="text-neutral-400 font-bold">Responsável:</span> {comment.decidedBy || comment.author}</div>
                                          <div><span className="text-neutral-400 font-bold">Aprovadores:</span> {comment.approvedBy || 'Diretoria'}</div>
                                          <div><span className="text-neutral-400 font-bold">Impacto Estimado:</span> <span className="text-purple-700 font-black">{comment.impact || 'Médio'}</span></div>
                                          <div><span className="text-neutral-400 font-bold">Relacionamentos:</span> {comment.relationships?.join(', ') || 'Nenhum'}</div>
                                        </div>
                                      </div>
                                    )}

                                    {/* SUGGESTION / INTERACTIVE VOTING BLOCK */}
                                    {comment.type === 'idea' && comment.voting && (
                                      <div className="mb-4 bg-amber-50/20 border border-amber-100 rounded-2xl p-4 text-left">
                                        <span className="text-[10px] font-black uppercase text-amber-700 tracking-wider block mb-2.5">
                                          Votação Estruturada do Refinamento:
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                          <button
                                            onClick={() => handleToggleStructuredVote(comment.id, 'agree')}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${
                                              comment.voting.userVote === 'agree'
                                                ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                                : 'bg-white hover:bg-amber-50/30 text-amber-800 border-neutral-200'
                                            }`}
                                          >
                                            <span>👍 Concordo</span>
                                            <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-black ${comment.voting.userVote === 'agree' ? 'bg-amber-600' : 'bg-neutral-100'}`}>
                                              {comment.voting.agree}
                                            </span>
                                          </button>

                                          <button
                                            onClick={() => handleToggleStructuredVote(comment.id, 'greatIdea')}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${
                                              comment.voting.userVote === 'greatIdea'
                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                                : 'bg-white hover:bg-indigo-50/30 text-indigo-700 border-neutral-200'
                                            }`}
                                          >
                                            <span>💡 Excelente Ideia</span>
                                            <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-black ${comment.voting.userVote === 'greatIdea' ? 'bg-indigo-700' : 'bg-neutral-100'}`}>
                                              {comment.voting.greatIdea}
                                            </span>
                                          </button>

                                          <button
                                            onClick={() => handleToggleStructuredVote(comment.id, 'disagree')}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${
                                              comment.voting.userVote === 'disagree'
                                                ? 'bg-rose-500 text-white border-rose-500 shadow-sm'
                                                : 'bg-white hover:bg-rose-50/30 text-rose-700 border-neutral-200'
                                            }`}
                                          >
                                            <span>👎 Discordo</span>
                                            <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-black ${comment.voting.userVote === 'disagree' ? 'bg-rose-600' : 'bg-neutral-100'}`}>
                                              {comment.voting.disagree}
                                            </span>
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* ATTACHMENTS (DOCUMENTS / IMAGES) MODULE */}
                                    {comment.attachments && comment.attachments.length > 0 && (
                                      <div className="mb-4 flex flex-col gap-2 text-left">
                                        {comment.attachments.map((file, idx) => {
                                          const isImg = file.type === 'image';
                                          return (
                                            <div key={idx} className="flex flex-col gap-2">
                                              {isImg && file.url && (
                                                <div className="w-full max-h-56 overflow-hidden rounded-xl border border-neutral-100 mb-1 shadow-inner relative group">
                                                  <img src={file.url} alt={file.name} className="w-full h-full object-cover group-hover:scale-102 transition-all duration-300" referrerPolicy="no-referrer" />
                                                </div>
                                              )}
                                              <div className="flex justify-between items-center bg-neutral-50 border border-neutral-150/60 p-3 rounded-xl hover:bg-neutral-150/20 transition-all">
                                                <div className="flex items-center gap-2">
                                                  <div className="w-8 h-8 rounded-lg bg-white shadow-2xs border border-neutral-100 flex items-center justify-center text-sm font-black text-neutral-500">
                                                    {file.type === 'pdf' ? '📄' : isImg ? '🖼' : file.type === 'xlsx' ? '📊' : '📄'}
                                                  </div>
                                                  <div className="flex flex-col">
                                                    <span className="text-xs font-bold text-neutral-700 leading-tight">{file.name}</span>
                                                    <span className="text-[9px] text-neutral-400 font-bold">{file.size}</span>
                                                  </div>
                                                </div>
                                                <button
                                                  onClick={() => showSuccess(`Baixando arquivo: ${file.name}`)}
                                                  className="text-[10px] bg-white hover:bg-indigo-50 hover:text-indigo-600 border border-neutral-200 rounded-lg px-2.5 py-1.5 font-bold cursor-pointer shadow-2xs transition-all"
                                                >
                                                  Download
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* DEPLOY CONSOLE MODULE */}
                                    {comment.type === 'deploy' && (
                                      <div className="mb-4 bg-neutral-900 border border-neutral-800 text-neutral-100 font-mono text-[10px] p-3.5 rounded-xl flex flex-col gap-1 text-left leading-relaxed">
                                        <div className="flex items-center gap-2 text-emerald-400 font-bold border-b border-neutral-800 pb-1.5 mb-1.5">
                                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                          <span>DEPLOYMENT SUCCESSFUL</span>
                                        </div>
                                        <div><span className="text-neutral-500">PROVIDER:</span> AWS ECS (Fargate)</div>
                                        <div><span className="text-neutral-500">CONTAINER:</span> cyzor-neural-core:rc-1.4.0</div>
                                        <div><span className="text-neutral-500">REPLICA_SET:</span> 2 instances running perfectly</div>
                                        <div><span className="text-neutral-500">TRAFFIC:</span> 100% routed successfully</div>
                                      </div>
                                    )}

                                    {/* AI GLOWING INSIGHT CARD */}
                                    {comment.type === 'ai' && (
                                      <div className="mb-4 bg-linear-to-r from-indigo-50/40 via-purple-50/30 to-white border border-indigo-200 rounded-xl p-4 text-left relative overflow-hidden shadow-2xs">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-200/10 rounded-full blur-xl pointer-events-none" />
                                        <div className="flex items-center gap-1.5 text-indigo-700 font-black text-xs mb-1">
                                          <Sparkles size={13} className="animate-pulse" />
                                          <span>Cyzor Neural Cognitive Summary</span>
                                        </div>
                                        <span className="text-[11px] text-neutral-500 font-semibold leading-relaxed">
                                          Este incidente impacta 2 tarefas críticas da Sprint e pode elevar a sobrecarga técnica. Recomenda-se acionar o sponsorship executivo para reprogramação preventiva.
                                        </span>
                                        <div className="flex gap-2 mt-3">
                                          <button
                                            onClick={() => handleQuickAction(comment.id, 'task')}
                                            className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black rounded-lg transition-all shadow-2xs"
                                          >
                                            Corrigir Escopo
                                          </button>
                                          <button
                                            onClick={() => showSuccess('Análise de impactos gerada e enviada por e-mail!')}
                                            className="px-2.5 py-1 bg-white hover:bg-neutral-50 text-neutral-600 text-[10px] border border-neutral-200 font-bold rounded-lg transition-all"
                                          >
                                            Simular Cenários
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* CORPORATE REACTIONS DISPLAY */}
                                    {comment.corporateReactions && (
                                      <div className="flex flex-wrap gap-1.5 items-center my-3 pl-1">
                                        {[
                                          { id: 'agree', icon: '👍', label: 'Concordo' },
                                          { id: 'excellent', icon: '🚀', label: 'Excelente' },
                                          { id: 'goodIdea', icon: '💡', label: 'Boa ideia' },
                                          { id: 'attention', icon: '⚠', label: 'Atenção' },
                                          { id: 'approved', icon: '✅', label: 'Aprovado' },
                                          { id: 'review', icon: '❓', label: 'Revisar' },
                                          { id: 'priority', icon: '🎯', label: 'Prioritário' }
                                        ].map(rx => {
                                          const count = (comment.corporateReactions as any)[rx.id] || 0;
                                          const userReacted = (comment.corporateReactions?.userReactions || []).includes(rx.id);
                                          if (count === 0 && !userReacted) return null;
                                          return (
                                            <button
                                              key={rx.id}
                                              onClick={() => handleToggleReaction(comment.id, rx.id)}
                                              title={rx.label}
                                              className={`px-2 py-1 rounded-lg text-[10px] font-extrabold flex items-center gap-1 transition-all cursor-pointer border ${
                                                userReacted
                                                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-black shadow-2xs'
                                                  : 'bg-neutral-50/50 hover:bg-neutral-100 border-neutral-150/60 text-neutral-500'
                                              }`}
                                            >
                                              <span>{rx.icon}</span>
                                              <span>{count}</span>
                                            </button>
                                          );
                                        })}

                                        {/* Trigger Reaction Popover */}
                                        <div className="relative">
                                          <button
                                            onClick={() => setActiveReactionPickerId(activeReactionPickerId === comment.id ? null : comment.id)}
                                            className="w-6 h-6 rounded-lg bg-neutral-50 hover:bg-neutral-100 text-[10px] text-neutral-400 flex items-center justify-center border border-neutral-200/60 cursor-pointer font-bold"
                                            title="Reagir"
                                          >
                                            ＋
                                          </button>

                                          {activeReactionPickerId === comment.id && (
                                            <div className="absolute bottom-8 left-0 bg-white p-1.5 rounded-2xl border border-neutral-150/60 shadow-xl flex gap-1.5 z-40 animate-in fade-in slide-in-from-bottom-2 duration-150">
                                              {[
                                                { id: 'agree', icon: '👍', label: 'Concordo' },
                                                { id: 'excellent', icon: '🚀', label: 'Excelente' },
                                                { id: 'goodIdea', icon: '💡', label: 'Boa ideia' },
                                                { id: 'attention', icon: '⚠', label: 'Atenção' },
                                                { id: 'approved', icon: '✅', label: 'Aprovado' },
                                                { id: 'review', icon: '❓', label: 'Revisar' },
                                                { id: 'priority', icon: '🎯', label: 'Prioritário' }
                                              ].map(rx => (
                                                <button
                                                  key={rx.id}
                                                  onClick={() => handleToggleReaction(comment.id, rx.id)}
                                                  className="hover:scale-120 p-1.5 hover:bg-neutral-50 rounded-lg text-sm transition-all cursor-pointer"
                                                  title={rx.label}
                                                >
                                                  {rx.icon}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Action Buttons Row */}
                                    <div className="grid grid-cols-4 border-t border-b border-neutral-100 py-1 text-[11px] mt-2">
                                      <button
                                        onClick={() => handleToggleReaction(comment.id, 'agree')}
                                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded-xl hover:bg-neutral-50 font-black cursor-pointer transition-all active:scale-95 ${
                                          (comment.corporateReactions?.userReactions || []).includes('agree') ? 'text-indigo-600' : 'text-neutral-500 hover:text-neutral-800'
                                        }`}
                                      >
                                        <Heart size={13} className={(comment.corporateReactions?.userReactions || []).includes('agree') ? 'fill-indigo-600' : ''} />
                                        <span>Gostei</span>
                                      </button>

                                      <button
                                        onClick={() => setActiveReplyBoxCommentId(isExpanded ? null : comment.id)}
                                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded-xl hover:bg-neutral-50 font-black cursor-pointer transition-all ${
                                          isExpanded ? 'text-indigo-600 bg-indigo-50/50' : 'text-neutral-500 hover:text-neutral-800'
                                        }`}
                                      >
                                        <MessageSquare size={13} />
                                        <span>Responder</span>
                                      </button>

                                      <button
                                        onClick={() => handleShareComment(comment)}
                                        className="flex items-center justify-center gap-1.5 py-1.5 rounded-xl hover:bg-neutral-50 font-black text-neutral-500 hover:text-neutral-800 cursor-pointer transition-all"
                                      >
                                        <Share2 size={13} />
                                        <span>Compartilhar</span>
                                      </button>

                                      <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="flex items-center justify-center gap-1.5 py-1.5 rounded-xl hover:bg-rose-50 font-black text-neutral-500 hover:text-rose-600 cursor-pointer transition-all"
                                      >
                                        <Trash2 size={13} />
                                        <span>Excluir</span>
                                      </button>

                                      {/* Dropdown Options or Quick Actions trigger */}
                                      <div className="relative group/opt flex items-center justify-center">
                                        <button className="flex items-center justify-center gap-1.5 py-1.5 w-full rounded-xl hover:bg-neutral-50 font-black text-neutral-500 hover:text-neutral-800 cursor-pointer transition-all">
                                          <Settings size={13} />
                                          <span>Ações</span>
                                        </button>
                                        <div className="absolute right-0 bottom-8 hidden group-hover/opt:flex flex-col bg-white p-1 rounded-xl border border-neutral-200/80 shadow-lg min-w-[170px] z-30 font-bold text-left animate-in fade-in slide-in-from-bottom-1 duration-150">
                                          <button
                                            onClick={() => handleTogglePinComment(comment.id)}
                                            className="px-3 py-2 hover:bg-neutral-50 rounded-lg text-[10px] text-neutral-700 hover:text-indigo-600 transition-colors flex items-center gap-1.5 w-full"
                                          >
                                            📌 {comment.isPinned ? 'Desfixar Post' : 'Fixar no Topo'}
                                          </button>
                                          <button
                                            onClick={() => handleQuickAction(comment.id, 'task')}
                                            className="px-3 py-2 hover:bg-neutral-50 rounded-lg text-[10px] text-neutral-700 hover:text-indigo-600 transition-colors flex items-center gap-1.5 w-full"
                                          >
                                            ✅ Criar Tarefa Cyzor
                                          </button>
                                          <button
                                            onClick={() => handleQuickAction(comment.id, 'doc')}
                                            className="px-3 py-2 hover:bg-neutral-50 rounded-lg text-[10px] text-neutral-700 hover:text-indigo-600 transition-colors flex items-center gap-1.5 w-full"
                                          >
                                            📄 Criar Documento
                                          </button>
                                          {comment.type !== 'decision' && (
                                            <button
                                              onClick={() => handleQuickAction(comment.id, 'decision')}
                                              className="px-3 py-2 hover:bg-neutral-50 rounded-lg text-[10px] text-neutral-700 hover:text-indigo-600 transition-colors flex items-center gap-1.5 w-full"
                                            >
                                              ⚡ Marcar como Decisão
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Inline Replies Thread area (LinkedIn-style) */}
                                    {(isExpanded || (comment.replies && comment.replies.length > 0)) && (
                                      <div className="flex flex-col gap-4 pl-2 sm:pl-4 mt-1">
                                        {comment.replies && comment.replies.length > 0 && (
                                          <div className="flex flex-col gap-3 relative before:absolute before:left-3.5 before:top-2 before:bottom-2 before:w-[1.5px] before:bg-neutral-100">
                                            {comment.replies.map((reply) => (
                                              <div key={reply.id} className="flex flex-col gap-2 pl-6 relative">
                                                <div className="flex gap-2.5 items-start">
                                                  {/* Mini avatar */}
                                                  <div className="w-7 h-7 rounded-full bg-neutral-100 border border-neutral-200 flex items-center justify-center text-[10px] font-extrabold overflow-hidden shrink-0 shadow-2xs absolute left-0 top-0.5">
                                                    {reply.photo ? (
                                                      <img src={reply.photo} alt={reply.author} className="w-full h-full object-cover" />
                                                    ) : (
                                                      reply.author.charAt(0)
                                                    )}
                                                  </div>
                                                  
                                                  {/* Reply text bubble */}
                                                  <div className="flex-1 bg-neutral-50/70 p-3 rounded-2xl border border-neutral-100 text-left">
                                                    <div className="flex justify-between items-start mb-1">
                                                      <div className="flex flex-col">
                                                        <div className="flex items-center gap-1.5">
                                                          <span className="text-[11px] font-black text-neutral-800">{reply.author}</span>
                                                          <span className="text-[9px] text-neutral-400 font-semibold">{reply.time}</span>
                                                        </div>
                                                        <span className="text-[8px] text-neutral-400 font-bold leading-none mt-0.5">{reply.role}</span>
                                                      </div>
                                                    </div>
                                                    <p className="text-[11px] sm:text-xs text-neutral-600 font-semibold leading-relaxed">
                                                      {reply.text}
                                                    </p>
                                                  </div>
                                                </div>

                                                {/* Reply of Reply (Level 2) */}
                                                {reply.replies && reply.replies.length > 0 && (
                                                  <div className="flex flex-col gap-2.5 mt-1 pl-4 border-l border-neutral-100/60 ml-2">
                                                    {reply.replies.map((subReply) => (
                                                      <div key={subReply.id} className="flex gap-2 items-start">
                                                        <div className="w-5 h-5 rounded-full bg-neutral-150 border border-neutral-200 flex items-center justify-center text-[8px] font-extrabold overflow-hidden shrink-0">
                                                          {subReply.photo ? (
                                                            <img src={subReply.photo} alt={subReply.author} className="w-full h-full object-cover" />
                                                          ) : (
                                                            subReply.author.charAt(0)
                                                          )}
                                                        </div>
                                                        <div className="flex-1 bg-neutral-100/40 p-2 rounded-xl text-left">
                                                          <div className="flex items-center gap-1 mb-0.5">
                                                            <span className="text-[10px] font-black text-neutral-800">{subReply.author}</span>
                                                            <span className="text-[8px] text-neutral-400 font-bold">{subReply.time}</span>
                                                          </div>
                                                          <p className="text-[10px] sm:text-[11px] text-neutral-600 font-semibold leading-tight">
                                                            {subReply.text}
                                                          </p>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Quick inline reply input box */}
                                        <div className="flex gap-2 items-center pl-0 sm:pl-6 mt-1">
                                          <div className="w-6 h-6 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 font-black text-[9px] flex items-center justify-center overflow-hidden shrink-0 shadow-2xs">
                                            {getMemberPhoto('João Silva') ? (
                                              <img src={getMemberPhoto('João Silva')!} alt="JS" className="w-full h-full object-cover" />
                                            ) : (
                                              'JS'
                                            )}
                                          </div>
                                          <div className="flex-1 flex gap-2">
                                            <input
                                              type="text"
                                              value={newReplyTexts[comment.id] || ''}
                                              onChange={(e) => setNewReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  handleAddReply(comment.id);
                                                }
                                              }}
                                              placeholder={`Responder para ${comment.author.split(' ')[0]}...`}
                                              className="flex-1 px-3 py-1.5 bg-neutral-50 hover:bg-neutral-100/50 focus:bg-white border border-neutral-200 focus:border-indigo-500 rounded-xl text-xs text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-indigo-100 font-semibold transition-all"
                                            />
                                            <button
                                              onClick={() => handleAddReply(comment.id)}
                                              className="px-3 py-1.5 bg-neutral-100 hover:bg-indigo-600 hover:text-white text-neutral-500 text-[10px] font-black rounded-xl transition-all cursor-pointer flex items-center justify-center"
                                            >
                                              <Send size={10} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </motion.div>
                                );
                              })}
                            </AnimatePresence>
                          </div>
                        </div>
                      );
                    })}

                    {filteredComments.length === 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white p-10 rounded-2xl border border-neutral-100 text-center flex flex-col items-center gap-3"
                      >
                        <div className="w-12 h-12 rounded-full bg-neutral-50 flex items-center justify-center text-neutral-400">
                          <MessageSquare size={20} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-black uppercase text-neutral-800">Nenhuma atividade encontrada</span>
                          <span className="text-[10px] text-neutral-400 font-semibold max-w-xs leading-normal">
                            Tente refinar sua pesquisa ou mudar os filtros da Central de Colaboração.
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Right side widgets: Social Analytics & trending hashtags */}
                <div className="flex flex-col gap-5">
                  
                  {/* Cyzor Neural IA Summary (Simulated Future Capability) */}
                  <div className="bg-linear-to-b from-indigo-900 to-slate-900 text-white p-5 rounded-2xl shadow-sm border border-indigo-950 flex flex-col gap-3.5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-black uppercase text-indigo-200 tracking-wider flex items-center gap-1">
                          🤖 Cyzor Neural
                        </span>
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      </div>
                      <span className="text-[9px] bg-indigo-800 text-indigo-200 font-extrabold px-2 py-0.5 rounded-lg border border-indigo-700">
                        Workspace Active
                      </span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-indigo-300 font-black uppercase tracking-widest leading-none">Análise Cognitiva do Projeto</span>
                      <p className="text-[11px] font-medium text-indigo-100/90 leading-relaxed mt-1">
                        "Foram homologadas 2 decisões de infraestrutura e alinhamento de custos. Alerta neural: Há risco de sobrecarga técnica na Sprint 3. Alinhamento de objetivos da Squad estimado em 95.8%."
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-center text-xs border-t border-indigo-800/60 pt-3">
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: '🤖 Gerando Resumo IA...',
                            html: '<div class="flex flex-col items-center gap-2 font-sans py-4"><span class="animate-spin text-indigo-600 text-xl">⏳</span><span class="text-xs text-neutral-500 font-semibold">Analisando histórico de atividades do feed...</span></div>',
                            showConfirmButton: false,
                            timer: 2000
                          }).then(() => {
                            Swal.fire({
                              title: 'Resumo das Atividades Recentes',
                              html: `
                                <div class="text-left font-sans text-xs leading-relaxed text-neutral-700">
                                  <p class="mb-2 font-bold text-neutral-900">⚡ Decisões Homologadas:</p>
                                  <ul class="list-disc pl-4 mb-3 font-semibold">
                                    <li>Alocação de GPU/AWS aprovada por Carlos Executive.</li>
                                    <li>Previsibilidade de faturamento homologada com acurácia de 94.2%.</li>
                                  </ul>
                                  <p class="mb-2 font-bold text-neutral-900">⚠️ Conflitos & Alertas:</p>
                                  <ul class="list-disc pl-4 mb-3 font-semibold">
                                    <li>Divergência entre Roadmap e Kanban (resolvido automaticamente pelo deployer).</li>
                                  </ul>
                                  <p class="font-bold text-emerald-600">💡 Próximos Passos recomendados pelo Cyzor:</p>
                                  <p class="font-semibold italic">Agendar homologação técnica das heurísticas com clientes alfas (Sugerido por Maria Santos).</p>
                                </div>
                              `,
                              icon: 'success'
                            });
                          });
                        }}
                        className="px-2.5 py-1.5 bg-indigo-700/60 hover:bg-indigo-600 border border-indigo-700 rounded-xl text-[10px] font-extrabold text-white transition-all cursor-pointer"
                      >
                        Resumir Feed
                      </button>
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: '🤖 Analisando Riscos...',
                            html: '<div class="flex flex-col items-center gap-2 font-sans py-4"><span class="animate-spin text-amber-600 text-xl">⏳</span><span class="text-xs text-neutral-500 font-semibold">Buscando potenciais conflitos de código e cronograma...</span></div>',
                            showConfirmButton: false,
                            timer: 1800
                          }).then(() => {
                            Swal.fire({
                              title: 'Relatório Neural de Riscos',
                              html: `
                                <div class="text-left font-sans text-xs leading-relaxed text-neutral-700">
                                  <p class="font-black text-amber-600 mb-2">🔴 Risco Crítico Encontrado:</p>
                                  <p class="font-bold mb-3">O modelo de rede neural possui alta demanda de memória. Sem auto-scaling ativado de forma robusta, podemos enfrentar latência na esteira de produção durante horário comercial.</p>
                                  <p class="font-bold text-neutral-800">Ação mitigatória recomendada:</p>
                                  <p class="font-semibold bg-neutral-50 p-2 rounded-xl border">Configurar trigger de escala quando CPU atingir 75% na AWS.</p>
                                </div>
                              `,
                              icon: 'warning'
                            });
                          });
                        }}
                        className="px-2.5 py-1.5 bg-white text-neutral-900 hover:bg-neutral-50 rounded-xl text-[10px] font-extrabold transition-all cursor-pointer"
                      >
                        Analisar Riscos
                      </button>
                    </div>
                  </div>

                  {/* Feed Statistics Board */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4 text-left">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Métricas de Colaboração</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-neutral-50 border border-neutral-100 rounded-xl flex flex-col">
                        <span className="text-[9px] font-extrabold text-neutral-400 uppercase tracking-widest">Interações</span>
                        <span className="text-lg font-black text-neutral-800 mt-1">
                          {comments.reduce((acc, curr) => acc + curr.votes, 0) + comments.reduce((acc, curr) => acc + (curr.replies?.length || 0), 0)}
                        </span>
                      </div>
                      <div className="p-3 bg-purple-50/30 border border-purple-100/40 rounded-xl flex flex-col">
                        <span className="text-[9px] font-extrabold text-purple-600 uppercase tracking-widest">Decisões</span>
                        <span className="text-lg font-black text-purple-700 mt-1">
                          {comments.filter(c => c.type === 'decision' || c.isPinned).length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ITENS FIXADOS (Announcement / Important Decisions Board) */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-3 text-left">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Últimas Decisões & Avisos</h3>
                    <div className="flex flex-col gap-2.5">
                      {comments.filter(c => c.type === 'decision' || c.isPinned).slice(0, 3).map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            setSearchQuery(item.text.slice(0, 20));
                            showSuccess(`Filtrando por decisão: "${item.text.slice(0, 30)}..."`);
                          }}
                          className="p-3 rounded-xl bg-purple-50/20 hover:bg-purple-50/50 border border-purple-100/40 text-left transition-all cursor-pointer flex flex-col gap-1 w-full"
                        >
                          <div className="flex justify-between items-center text-[9px] font-black uppercase text-purple-700">
                            <span>⚡ Decisão</span>
                            <span>{item.time}</span>
                          </div>
                          <span className="text-[11px] font-semibold text-neutral-700 line-clamp-2 leading-snug">
                            {item.text}
                          </span>
                        </button>
                      ))}
                      {comments.filter(c => c.type === 'decision' || c.isPinned).length === 0 && (
                        <span className="text-[10px] text-neutral-400 italic">Nenhum registro fixado no momento.</span>
                      )}
                    </div>
                  </div>

                  {/* Recent Files Widget */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-3 text-left">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Arquivos Recentes</h3>
                    <div className="flex flex-col gap-2">
                      {comments.flatMap(c => c.attachments || []).slice(0, 3).map((file, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 rounded-xl hover:bg-neutral-50 border border-neutral-100 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-base">{file.type === 'pdf' ? '📄' : file.type === 'image' ? '🖼' : '📊'}</span>
                            <div className="flex flex-col leading-tight">
                              <span className="font-bold text-neutral-800 max-w-[130px] truncate">{file.name}</span>
                              <span className="text-[8px] text-neutral-400 font-bold">{file.size}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => showSuccess(`Baixando ${file.name}`)}
                            className="text-[9px] bg-neutral-50 hover:bg-indigo-50 hover:text-indigo-600 px-2 py-1 rounded-md border text-neutral-500 font-extrabold transition-all"
                          >
                            Baixar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trending Tags (Dynamic Sidebar Widget) */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-3 text-left">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Tópicos Populares</h3>
                      {discussionFilterTag && (
                        <button 
                          onClick={() => setDiscussionFilterTag(null)}
                          className="text-[9px] font-bold text-indigo-600 hover:underline cursor-pointer"
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {popularTags.length === 0 ? (
                        <span className="text-[10px] text-neutral-400 font-semibold italic">Nenhuma hashtag ativa</span>
                      ) : (
                        popularTags.map(({ tag, count }) => {
                          const isActive = discussionFilterTag === tag;
                          return (
                            <button
                              key={tag}
                              onClick={() => setDiscussionFilterTag(isActive ? null : tag)}
                              className={`px-2.5 py-1 rounded-lg text-xs transition-all cursor-pointer border ${
                                isActive
                                  ? 'bg-indigo-600 text-white font-black border-indigo-600'
                                  : 'bg-neutral-50 hover:bg-neutral-100 text-neutral-700 font-semibold border-neutral-200/50'
                              }`}
                            >
                              <span className={isActive ? 'text-white' : 'text-indigo-600 font-bold mr-1'}>{tag}</span>
                              <span className={`text-[9px] px-1 py-0.1 rounded-full font-extrabold ${
                                isActive ? 'bg-indigo-700 text-white' : 'bg-neutral-200/60 text-neutral-500'
                              }`}>
                                {count}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Active Collaborators list with interactive ping feedback */}
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4 text-left">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Participantes Ativos</h3>
                    
                    <div className="flex flex-col gap-3">
                      {projectMembers.map((member: any) => (
                        <div key={member.id} className="flex items-center text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-neutral-100 border border-neutral-200 overflow-hidden shrink-0 relative">
                              {member.avatarUrl ? (
                                <img src={member.avatarUrl} alt={member.name || 'Membro'} className="w-full h-full object-cover" />
                              ) : (
                                (member.name || '?').charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-extrabold text-neutral-800 leading-tight">{member.name || 'Membro'}</span>
                              <span className="text-[9px] text-neutral-400 font-semibold leading-none mt-0.5">{member.role || 'Função não definida'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {projectMembers.length === 0 && <span className="text-[10px] text-neutral-400 font-semibold">Nenhum membro alocado ao projeto.</span>}
                    </div>
                  </div>

                </div>

              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                6. TAB: TAREFAS LIST TABLE
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'tarefas' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-black text-neutral-900">Lista de Execução</h3>
                    <p className="text-xs text-neutral-500 font-bold">A mesma entidade Task do Board, com filtros e ordenação.</p>
                  </div>
                  <button onClick={() => setIsAddTaskModalOpen(true)} className="px-3.5 py-1.5 bg-neutral-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-black transition-all cursor-pointer">
                    <Plus size={14} /> Registrar Atividade
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                  <input value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} placeholder="Buscar tarefa..." className="xl:col-span-2 bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950" />
                  <select value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold text-neutral-800 outline-none">
                    <option value="">Todos os status</option><option value="TODO">Backlog</option><option value="IN_PROGRESS">Em andamento</option><option value="REVIEW">Review / Teste</option><option value="DONE">Concluído</option><option value="BLOCKED">Bloqueado</option>
                  </select>
                  <select value={taskPriorityFilter} onChange={(e) => setTaskPriorityFilter(e.target.value)} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold text-neutral-800 outline-none">
                    <option value="">Todas as prioridades</option><option value="CRITICAL">Crítica</option><option value="HIGH">Alta</option><option value="MEDIUM">Média</option><option value="LOW">Baixa</option>
                  </select>
                  <select value={taskAssigneeFilter} onChange={(e) => setTaskAssigneeFilter(e.target.value)} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold text-neutral-800 outline-none">
                    <option value="">Todos os responsáveis</option>
                    {Array.from(new Set(projectTasks.map((task: any) => task.assigneeName).filter(Boolean))).map((assignee: any) => <option key={assignee} value={assignee}>{assignee}</option>)}
                  </select>
                  <select value={taskSort} onChange={(e) => setTaskSort(e.target.value as typeof taskSort)} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 text-xs font-semibold text-neutral-800 outline-none">
                    <option value="createdAt">Mais recentes</option><option value="dueDate">Prazo</option><option value="priority">Prioridade</option>
                  </select>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead><tr className="text-[10px] font-black uppercase text-neutral-400 border-b border-neutral-100"><th className="pb-3">Tarefa</th><th className="pb-3">Status</th><th className="pb-3">Prioridade</th><th className="pb-3">Responsável</th><th className="pb-3">Prazo</th><th className="pb-3 text-right">Ações</th></tr></thead>
                    <tbody>
                      {executionTasks.map((t: any) => (
                        <tr key={t.id} className="border-b border-neutral-50 last:border-none hover:bg-neutral-50/50 transition-colors">
                          <td className="py-3 text-xs font-bold text-neutral-800">{t.title}</td>
                          <td className="py-3"><span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${t.status === 'DONE' ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-50 text-neutral-500'}`}>{t.status || 'Pendente'}</span></td>
                          <td className="py-3"><span className={`text-[9px] font-bold px-2 py-0.5 rounded ${t.priority === 'HIGH' || t.priority === 'CRITICAL' ? 'bg-rose-50 text-rose-700' : 'bg-neutral-50 text-neutral-500'}`}>{t.priority || 'Não definida'}</span></td>
                          <td className="py-3 text-xs text-neutral-600">{t.assigneeName || 'Não atribuído'}</td>
                          <td className="py-3 text-xs text-neutral-500">{t.dueDate ? new Date(t.dueDate).toLocaleDateString('pt-BR') : 'Sem prazo'}</td>
                          <td className="py-3 text-right"><button onClick={() => handleDeleteTask(t.id)} className="text-neutral-400 hover:text-rose-600 p-1.5" title="Remover"><Trash2 size={13} /></button></td>
                        </tr>
                      ))}
                      {executionTasks.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-xs text-neutral-400 font-bold">Nenhuma tarefa encontrada com os filtros atuais.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                7. TAB: DOCUMENTAÇÃO
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'documentacao' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                <div className="bg-white border border-neutral-100 rounded-2xl p-5 flex flex-col gap-4 h-fit">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-black text-neutral-800 uppercase tracking-wider">Documentos Técnicos</h3>
                    <button
                      onClick={() => setIsAddDocModalOpen(true)}
                      className="p-1 hover:bg-neutral-100 text-neutral-700 rounded transition-colors cursor-pointer"
                      title="Publicar Documento"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    {projectDocs.map((doc) => (
                      <div key={doc.id} className="p-3 border border-neutral-50 rounded-xl hover:border-neutral-200 transition-colors cursor-pointer bg-[#FAFAFA]/50">
                        <span className="text-xs font-bold text-neutral-800 block truncate">{doc.filename || doc.title || 'Documento'}</span>
                        <span className="text-[9px] text-neutral-400 font-bold block mt-1">
                          {doc.size || '—'} • {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('pt-BR') : 'Geral'}
                        </span>
                      </div>
                    ))}

                    {projectDocs.length === 0 && (
                      <p className="text-[10px] text-neutral-400 font-bold text-center py-6">Nenhum documento anexado.</p>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-4">
                  <h3 className="text-xs font-black text-neutral-800 uppercase tracking-wider">Visualizador de Documentos</h3>
                  <div className="border border-neutral-50 p-6 rounded-2xl bg-neutral-50/30 text-xs text-neutral-600 font-medium">
                    <p className="mb-4">Selecione um arquivo de especificação técnica ou atas no menu ao lado para visualizar os dados completos.</p>
                    <p>Você também pode redigir notas integradas que ajudam a squad no alinhamento diário.</p>
                  </div>
                </div>

              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                8. TAB: ARQUIVOS
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'arquivos' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div>
                  <h3 className="text-base font-black text-neutral-900">Anexos e Arquivos</h3>
                  <p className="text-xs text-neutral-500 font-bold">Documentos, Mockups de Design e Planilhas do Projeto.</p>
                </div>

                {/* Drag and drop field */}
                <div 
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDropFile}
                  className="border-2 border-dashed border-neutral-200 hover:border-neutral-400 rounded-2xl p-8 text-center transition-colors flex flex-col items-center justify-center gap-2 bg-[#FAFAFA]/50"
                >
                  <Paperclip className="text-neutral-400 mb-1" size={24} />
                  <span className="text-xs font-bold text-neutral-800">Arraste e solte arquivos aqui para anexar</span>
                  <span className="text-[10px] text-neutral-400 font-semibold">Tamanho máximo recomendado: 50MB</span>
                </div>

                {/* File list */}
                <div className="flex flex-col gap-3">
                  <h4 className="text-xs font-black text-neutral-800 uppercase tracking-wider">Arquivos do Projeto ({files.length})</h4>
                  
                  <div className="flex flex-col gap-2">
                    {files.map((file, idx) => (
                      <div key={idx} className="p-3.5 border border-neutral-100 rounded-xl hover:border-neutral-200 transition-colors flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <span className="text-base">📁</span>
                          <div>
                            <span className="text-xs font-bold text-neutral-800 block">{file.name}</span>
                            <span className="text-[9px] text-neutral-400 font-bold">Upload por {file.uploader} • {file.date}</span>
                          </div>
                        </div>

                        <span className="text-xs font-black text-neutral-500 bg-neutral-50 border border-neutral-100 px-2 py-1 rounded">
                          {file.size}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                9. TAB: FINANCEIRO DO PROJETO
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'financeiro' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                  <div>
                    <h3 className="text-base font-black text-neutral-900">Extrato Financeiro do Projeto</h3>
                    <p className="text-xs text-neutral-500 font-bold">Lançamentos de faturamento, pagamentos de squads e licenças de software.</p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="text-[10px] font-black uppercase text-neutral-400 border-b border-neutral-100 pb-2">
                          <th className="pb-3">Descrição</th>
                          <th className="pb-3">Tipo</th>
                          <th className="pb-3">Valor</th>
                          <th className="pb-3">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectFinance.map((fin) => (
                          <tr key={fin.id} className="border-b border-neutral-50 last:border-none hover:bg-neutral-50/50 transition-colors">
                            <td className="py-3 text-xs font-bold text-neutral-800">{fin.description}</td>
                            <td className="py-3">
                              <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                                fin.type === 'RECEITA' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                              }`}>
                                {fin.type}
                              </span>
                            </td>
                            <td className="py-3 text-xs font-bold text-neutral-800">
                              R$ {Number(fin.amount || 0).toLocaleString('pt-BR')}
                            </td>
                            <td className="py-3 text-[10px] font-bold text-neutral-400">
                              {fin.date || 'Hoje'}
                            </td>
                          </tr>
                        ))}

                        {projectFinance.length === 0 && (
                          <tr>
                            <td colSpan={4} className="py-8 text-center text-xs text-neutral-400 font-bold">Nenhum faturamento registrado no ledger operacional deste projeto.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-3">
                    <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Orçamento do Projeto</span>
                    <h3 className="text-2xl font-black text-neutral-900">R$ {Number(project.budget || 0).toLocaleString('pt-BR')}</h3>
                    <div className="h-0.5 bg-neutral-100 w-full my-1" />
                    <span className="text-[10px] text-neutral-400 font-bold">Valor persistido no cadastro do projeto.</span>
                  </div>

                  <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-3">
                    <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Despesas Registradas</span>
                    <h3 className="text-2xl font-black text-rose-700">{projectFinance.length > 0 ? `R$ ${projectCostTotal.toLocaleString('pt-BR')}` : 'Não informado'}</h3>
                    <div className="h-0.5 bg-neutral-100 w-full my-1" />
                    <span className="text-[10px] text-neutral-400 font-bold">Soma dos lançamentos reais classificados como despesa.</span>
                  </div>
                </div>

              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                10. TAB: CYZOR INTELLIGENCE
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'ia' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <Sparkles size={18} className="text-neutral-400" />
                  <div>
                    <h3 className="text-base font-black text-neutral-900">CYZOR Intelligence</h3>
                    <p className="text-xs text-neutral-500 font-bold">A análise contextual deste projeto ainda não está disponível.</p>
                  </div>
                </div>
                <div className="border border-dashed border-neutral-200 p-8 rounded-2xl text-center text-xs text-neutral-500 font-semibold bg-neutral-50/30">
                  Nenhum serviço de inteligência conectado a este workspace. Os dados do projeto não serão interpretados ou simulados nesta área.
                </div>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
                11. TAB: CONFIGURAÇÕES E PARÂMETROS
                ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {activeTab === 'configuracoes' && (
              <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col gap-6">
                <div>
                  <h3 className="text-base font-black text-neutral-900">Parâmetros Operacionais</h3>
                  <p className="text-xs text-neutral-500 font-bold">Edite e reconfigure o orçamento, prioridades e cronogramas globais.</p>
                </div>

                <form onSubmit={handleSaveProjectInfo} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-neutral-600">Nome do Projeto</label>
                    <input 
                      type="text" 
                      value={editProjectName}
                      onChange={(e) => setEditProjectName(e.target.value)}
                      className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-neutral-600">Status Operacional</label>
                    <select 
                      value={editProjectStatus}
                      onChange={(e) => setEditProjectStatus(e.target.value)}
                      className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950"
                    >
                      <option value="Planejamento">Planejamento</option>
                      <option value="Em Desenvolvimento">Em Desenvolvimento</option>
                      <option value="Testes">Testes</option>
                      <option value="Em Produção">Em Produção</option>
                      <option value="Concluído">Concluído</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-neutral-600">Prioridade</label>
                    <select 
                      value={editProjectPriority}
                      onChange={(e) => setEditProjectPriority(e.target.value)}
                      className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950"
                    >
                      <option value="Média">Média</option>
                      <option value="Alta">Alta</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-neutral-600">Orçamento Previsto (R$)</label>
                    <input 
                      type="number" 
                      value={editProjectBudget}
                      onChange={(e) => setEditProjectBudget(Number(e.target.value))}
                      className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-neutral-600">Progresso Geral (%)</label>
                    <input 
                      type="number" 
                      min="0"
                      max="100"
                      value={editProjectProgress}
                      onChange={(e) => setEditProjectProgress(Number(e.target.value))}
                      className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none focus:border-neutral-950"
                    />
                  </div>

                  <div className="md:col-span-2 flex justify-end gap-2.5 mt-4">
                    <button 
                      type="submit"
                      className="px-5 py-2.5 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-black transition-colors cursor-pointer shadow-xs"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </form>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          MODAL: EDIT PROJECT REGISTER
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-neutral-200 rounded-2xl w-full max-w-md p-6 flex flex-col gap-4 text-left shadow-2xl"
          >
            <div className="flex justify-between items-center border-b border-neutral-100 pb-3">
              <h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Configurações Básicas</h3>
              <button onClick={() => setIsEditModalOpen(false)} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
            </div>

            <form onSubmit={handleSaveProjectInfo} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Nome do Projeto</label>
                <input 
                  type="text" 
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Progresso Geral (%)</label>
                <input 
                  type="number" 
                  min="0"
                  max="100"
                  value={editProjectProgress}
                  onChange={(e) => setEditProjectProgress(Number(e.target.value))}
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-neutral-200 hover:bg-neutral-50 rounded-xl text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Salvar
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          MODAL: ADD NEW KANBAN TASK
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isAddTaskModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-neutral-200 rounded-2xl w-full max-w-md p-6 flex flex-col gap-4 text-left shadow-2xl"
          >
            <div className="flex justify-between items-center border-b border-neutral-100 pb-3">
              <h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Adicionar Nova Tarefa</h3>
              <button onClick={() => setIsAddTaskModalOpen(false)} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
            </div>

            <form onSubmit={handleCreateTask} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Título da tarefa</label>
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="Ex: Desenvolver fluxo de verificação de schemas"
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                    required
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Descrição</label>
                  <textarea
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    placeholder="Contexto, objetivo e critérios de aceitação da tarefa"
                    className="min-h-[112px] resize-none bg-[#FAFAFA] border border-neutral-200 rounded-2xl px-4 py-3 text-xs font-semibold text-neutral-800 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Data de entrega</label>
                  <input
                    type="date"
                    value={newTaskDueDate}
                    onChange={(e) => setNewTaskDueDate(e.target.value)}
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Estimativa (horas)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={newTaskEstimateHours}
                    onChange={(e) => setNewTaskEstimateHours(e.target.value)}
                    placeholder="Ex: 2.5"
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Responsável</label>
                  <input
                    type="text"
                    value={newTaskAssignee}
                    onChange={(e) => setNewTaskAssignee(e.target.value)}
                    placeholder="Ex: João Silva"
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Prioridade</label>
                  <select
                    value={newTaskPriority}
                    onChange={(e) => setNewTaskPriority(e.target.value)}
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  >
                    <option value="Baixa">Baixa</option>
                    <option value="Média">Média</option>
                    <option value="Alta">Alta</option>
                    <option value="Crítica">Crítica</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Coluna inicial</label>
                  <select
                    value={newTaskStatus}
                    onChange={(e) => setNewTaskStatus(e.target.value)}
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  >
                    <option value="TODO">Backlog</option>
                    <option value="IN_PROGRESS">Em Andamento</option>
                    <option value="REVIEW">Review / Teste</option>
                    <option value="DONE">Concluído</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-neutral-600">Categoria</label>
                  <input
                    type="text"
                    value={newTaskCategory}
                    onChange={(e) => setNewTaskCategory(e.target.value)}
                    placeholder="Ex: Desenvolvimento, QA, Bug"
                    className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setIsAddTaskModalOpen(false)}
                  className="px-4 py-2 border border-neutral-200 hover:bg-neutral-50 rounded-xl text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Registrar tarefa
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          MODAL: PUBLISH DOCUMENT
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isAddDocModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-neutral-200 rounded-2xl w-full max-w-md p-6 flex flex-col gap-4 text-left shadow-2xl"
          >
            <div className="flex justify-between items-center border-b border-neutral-100 pb-3">
              <h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Anexar Ata ou Documento</h3>
              <button onClick={() => setIsAddDocModalOpen(false)} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
            </div>

            <form onSubmit={handleCreateDoc} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Título do Documento</label>
                <input 
                  type="text" 
                  value={newDocTitle}
                  onChange={(e) => setNewDocTitle(e.target.value)}
                  placeholder="Ex: Ata de Reunião de Alinhamento Técnico"
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Conteúdo do Documento (Markdown suportado)</label>
                <textarea 
                  value={newDocContent}
                  onChange={(e) => setNewDocContent(e.target.value)}
                  rows={4}
                  placeholder="Ex: Discussão sobre cronogramas da squad de front..."
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsAddDocModalOpen(false)}
                  className="px-4 py-2 border border-neutral-200 hover:bg-neutral-50 rounded-xl text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Publicar
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          MODAL: ADD MEMBER
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {isAddMemberModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-neutral-200 rounded-2xl w-full max-w-md p-6 flex flex-col gap-4 text-left shadow-2xl"
          >
            <div className="flex justify-between items-center border-b border-neutral-100 pb-3">
              <h3 className="text-sm font-black text-neutral-900 uppercase tracking-wider">Convidar para Squad</h3>
              <button onClick={() => setIsAddMemberModalOpen(false)} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
            </div>

            <form onSubmit={handleAddMember} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Nome Completo</label>
                <input 
                  type="text" 
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder="Ex: Rodrigo Albuquerque"
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-neutral-600">Cargo / Função</label>
                <select 
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  className="bg-[#FAFAFA] border border-neutral-200 rounded-xl px-4 py-2 text-xs font-semibold text-neutral-800 outline-none"
                >
                  <option value="Developer">Developer</option>
                  <option value="Designer">Designer</option>
                  <option value="Product Manager">Product Manager</option>
                  <option value="QA Engineer">QA Engineer</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsAddMemberModalOpen(false)}
                  className="px-4 py-2 border border-neutral-200 hover:bg-neutral-50 rounded-xl text-xs font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Convidar
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

    </div>
  );
}
